import { getMonthFromDate } from "../extensions/date";
import { titleCase } from "../extensions/extension";
import { GridcoDiscomData, IntrastateGen, GridoIsgs } from "../models/gridco";
import {
  RajasthanDiscomDrawal,
  RajasthanDiscomData,
  RajasthanGenData,
  RajasthanIsgs,
  RajasthanCollective,
  RajasthanGnaTgna,
  ReEnergyData,
  RajProcurementRecord,
  RajGenParams,
  RajasthanUploadFile,
  RajGenProfile,
  RajProcurement,
  RajTamData,
  RajBankingData,
  HarayanaDailyPeakData,
  DailyDiscomDrawalSchedule,
} from "../models/rajasthan";
import {
  buildHttpResponse,
  HttpMethod,
  buildMultipartHttpResponse,
} from "./Network_utility";
import { ApiResponse } from "../models/api_res";
/**
 * Recursively traverses an object/array and adds the given randomValue
 * to every numeric field (except dates and identifiers).
 * Excludes fields like 'id', 'block', 'year', 'month', and date-related fields.
 */
const discomMap: Record<string, string> = {
  JDVVNL: "Discom 3",
  JVVNL: "Discom 1",
  AVVNL: "Discom 2",
};

function addRandomToNumberFields(
  obj: any,
  randomValue: number,
  key?: string,
): any {
  // List of keys that should NOT be modified
  const excludeKeys = [
    "id",
    "date",
    "block",
    "year",
    "month",
    "slot",
    "station",
    "discom",
    "area",
    "divisional_headquarter",
    "district",
  ];

  if (typeof obj === "number") {
    // Skip modification if parent key is in exclude list
    if (key && excludeKeys.includes(key.toLowerCase())) {
      return obj;
    }
    // Add random value to all numeric fields (positive numbers)
    return obj >= 0 ? obj + randomValue : obj;
  } else if (Array.isArray(obj)) {
    return obj.map((item) => addRandomToNumberFields(item, randomValue, key));
  } else if (typeof obj === "object" && obj !== null) {
    const newObj: any = {};
    for (const objKey in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, objKey)) {
        newObj[objKey] = addRandomToNumberFields(
          obj[objKey],
          randomValue,
          objKey,
        );
      }
    }
    return newObj;
  }
  return obj;
}

/**
 * Helper to generate a random integer between 100 and 150 (inclusive).
 */
function generateRandomValue(): number {
  return Math.floor(Math.random() * (150 - 100 + 1)) + 100;
}

/**
 * Helper function to create demo name mappings for discom, area, and district
 * Returns maps that convert original API names to demo names
 */
function createDemoMaps(data: any[]): {
  discomMap: Record<string, string>;
  areaMap: Record<string, string>;
  districtMap: Record<string, string>;
} {
  const discomSet = new Set<string>();
  const areaSet = new Set<string>();
  const districtSet = new Set<string>();

  // Collect unique values from data
  data.forEach((item: any) => {
    if (item.discom) discomSet.add(item.discom);
    // API uses 'station' field for area names, not 'area'
    if (item.station) areaSet.add(item.station);
    if (item.divisional_headquarter)
      districtSet.add(item.divisional_headquarter);
    if (item.district) districtSet.add(item.district);
  });

  // Create mappings with demo names
  const discomMap: Record<string, string> = {};
  const areaMap: Record<string, string> = {};
  const districtMap: Record<string, string> = {};

  const discomArray = Array.from(discomSet);
  const areaArray = Array.from(areaSet);
  const districtArray = Array.from(districtSet);

  // Map original names to demo names (Discom 1, 2, 3)
  discomArray.forEach((name, index) => {
    discomMap[name] = `Discom ${index + 1}`;
  });

  // Map original station/area names to demo names (Area 1, 2, 3, ...)
  areaArray.forEach((name, index) => {
    areaMap[name] = `Area ${index + 1}`;
  });

  // Map original district names to demo names (District 1, 2, 3, ...)
  districtArray.forEach((name, index) => {
    districtMap[name] = `District ${index + 1}`;
  });

  return { discomMap, areaMap, districtMap };
}

function toArray<T>(res: any): T[] {
  if (Array.isArray(res)) return res;
  if (res && res.data && Array.isArray(res.data)) return res.data;
  return [];
}

export async function getRajasthanDiscomDrawlData({
  startDate,
  endDate,
}: {
  startDate?: string;
  endDate?: string;
}): Promise<RajasthanDiscomDrawal[]> {
  try {
    const randomValue = generateRandomValue();
    const res = await buildHttpResponse({
      endPoint: `data/getRajasthanDiscomDrawal?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });
    const response = toArray<any>(res);
    if (response.length === 0) return [];
    const mapped = response.map((item) => {
      return {
        date: item[1],
        month: getMonthFromDate(item[1]),
        block: item[2],
        area: item[3],
        mw: item[4],
        lu: item[4] / 400,
        discom: item[5],
        mwh: 0,
      };
    });

    const discoms: string[] = [];
    const area: string[] = [];
    for (let i = 0; i < mapped.length; i++) {
      const element = mapped[i].discom;
      const element2 = mapped[i].area;
      if (discoms.includes(element)) {
        continue;
      } else {
        discoms.push(element);
      }
      if (area.includes(element2)) {
        continue;
      } else {
        area.push(element2);
      }
    }

    const modifiedMapping = mapped.map((item) => {
      const discomIndex = discoms.indexOf(item.discom);
      const newDiscomName = `Discom ${discomIndex + 1}`;
      const areaIndex = area.indexOf(item.area);
      const newAreaName = `Area ${areaIndex + 1}`;
      return {
        ...item,
        discom: newDiscomName,
        area: newAreaName,
      };
    });

    return addRandomToNumberFields(modifiedMapping, randomValue);
  } catch (error) {
    console.error("Error fetching Rajasthan Discom Drawl data:", error);
    return [];
  }
}

export async function getRajasthanAreaForecastData({
  startDate,
  endDate,
}: {
  startDate?: string;
  endDate?: string;
}): Promise<RajasthanDiscomDrawal[]> {
  try {
    const randomValue = generateRandomValue();
    const res = await buildHttpResponse({
      endPoint: `rajasthan/getRajasthanAreaForecastData?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });
    const response = toArray<any>(res);
    if (response.length === 0) return [];


    const mapped = response.map((item) => {
      return {
        date: item[1],
        month: getMonthFromDate(item[1]),
        block: item[2],
        area: item[3],
        mw: item[4],
        discom: discomMap[item[5]] || item[5],
        mwh: 0,
      };
    });
    return addRandomToNumberFields(mapped, randomValue);
  } catch (error) {
    console.error("Error fetching Rajasthan Discom Drawl data:", error);
    return [];
  }
}

export async function getRajasthanDiscomData(
  startDate: string,
  endDate: string,
): Promise<RajasthanDiscomData[]> {
  try {
    const randomValue = generateRandomValue();

    const res = await buildHttpResponse({
      endPoint: `rajasthan/getRajasthanDiscomData?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    // ✅ API wraps data in { success, count, data: [...] }
    const response: RajasthanDiscomData[] = Array.isArray(res.data)
      ? res.data
      : [];
    if (response.length === 0) return [];

    const uniqueDates = Array.from(new Set(response.map((item) => item.date)));
const discomMap: Record<string, string> = {
  JDVVNL: "Discom 3",
  JVVNL: "Discom 1",
  AVVNL: "Discom 2",
};
    const mapped = response.map((item) => ({
      ...item,
      discom: discomMap[item.discom] || item.discom,
      date: item.date,
      block: item.block,

      drawal_mw: item.drawal_mw,
      drawal_mwh: item.drawal_mw / 4,
      drawal_lu: item.drawal_mw / 400,

      schedule_mw: item.schedule_mw,
      schedule_mwh: item.schedule_mw / 4,
      schedule_lu: item.schedule_mw / 400,

      load_shedding_mw: item.load_shedding_mw,
      load_shedding_mwh: item.load_shedding_mw / 4,
      load_shedding_lu: item.load_shedding_mw / 400,

      total_mw: item.drawal_mw + item.load_shedding_mw,
      total_mwh: (item.drawal_mw + item.load_shedding_mw) / 4,
      total_lu: (item.drawal_mw + item.load_shedding_mw) / 400,

      dateIndex: `${uniqueDates.indexOf(item.date) + 1} Day`,
      dates: `${startDate} to ${endDate}`,
      month: getMonthFromDate(item.date),
    }));

    return addRandomToNumberFields(mapped, randomValue);
  } catch (error) {
    console.error("Error fetching Rajasthan Discom Drawl data:", error);
    return [];
  }
}




export async function getRajasthanForecastDiscomData(
  startDate: string,
  endDate: string,
  model: string,
): Promise<RajasthanDiscomData[]> {
  try {
    const randomValue = generateRandomValue();

    const res = await buildHttpResponse({
      endPoint: `rajasthan/getDiscomForecast?start_date=${startDate}&end_date=${endDate}&model=${model}`,
      method: HttpMethod.GET,
    });

    // ✅ API wraps data in { success, count, data: [...] }
    const response: RajasthanDiscomData[] = Array.isArray(res.data)
      ? res.data
      : [];
    if (response.length === 0) return [];

    const uniqueDates = Array.from(new Set(response.map((item) => item.date)));
const discomMap: Record<string, string> = {
  JDVVNL: "Discom 3",
  JVVNL: "Discom 1",
  AVVNL: "Discom 2",
};
    const mapped = response.map((item) => ({
      ...item,
      discom: discomMap[item.discom] || item.discom,
      date: item.date,
      block: item.block,
      model: item.model,
      forcast_type: item.forcast_type, // "T+1" | "T+2"

      drawal_mw: item.drawal_mw,
      drawal_mwh: item.drawal_mw / 4,
      drawal_lu: item.drawal_mw / 400,

      schedule_mw: item.schedule_mw,
      schedule_mwh: item.schedule_mw / 4,
      schedule_lu: item.schedule_mw / 400,

      // Forecast has no load shedding
      load_shedding_mw: 0,
      load_shedding_mwh: 0,
      load_shedding_lu: 0,

      // Total = drawal only (no load shedding in forecast)
      total_mw: item.drawal_mw,
      total_mwh: item.drawal_mw / 4,
      total_lu: item.drawal_mw / 400,

      dateIndex: `${uniqueDates.indexOf(item.date) + 1} Day`,
      dates: `${startDate} to ${endDate}`,
      month: getMonthFromDate(item.date),
    }));

    return addRandomToNumberFields(mapped, randomValue);
  } catch (error) {
    console.error("Error fetching Rajasthan Discom Forecast data:", error);
    return [];
  }
}
export async function getDemoRajasthanDiscomDrawlData({
  startDate,
  endDate,
}: {
  startDate?: string;
  endDate?: string;
}): Promise<RajasthanDiscomDrawal[]> {
  try {
    const randomValue = generateRandomValue();

    const res = await buildHttpResponse({
      endPoint: `rajasthan/getRajasthanDiscomDrawal?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const response = toArray<any[]>(res);
    if (!response || response.length === 0) return [];

    // Columns: [date, slot, station, key_name, district, divisional_headquarter, discom, value_mw]
    //           [0]   [1]   [2]      [3]        [4]       [5]                     [6]     [7]

    // derive unique maps
    const uniqueDates = Array.from(new Set(response.map((item) => item[0])));
    const uniqueAreas = Array.from(new Set(response.map((item) => item[2]))); // station
    const uniqueDistricts = Array.from(
      new Set(response.map((item) => item[4])),
    ); // district
    const uniqueDivisions = Array.from(
      new Set(response.map((item) => item[5])),
    ); // divisional_headquarter

    // masked maps
    const discomMap: Record<string, string> = {
      JDVVNL: "Discom 3",
      JVVNL: "Discom 2",
      AVVNL: "Discom 1",
    };
    const areaMap: Record<string, string> = {};
    uniqueAreas.forEach((a, i) => (areaMap[a] = `Area ${i + 1}`));
    const districtMap: Record<string, string> = {};
    uniqueDistricts.forEach((d, i) => (districtMap[d] = `District ${i + 1}`));
    const divisionMap: Record<string, string> = {};
    uniqueDivisions.forEach((v, i) => (divisionMap[v] = `Division ${i + 1}`));

    const mapped = response.map((item) => {
      const mw = parseFloat(item[7]) || 0;
      return {
        dateIndex: (uniqueDates.indexOf(item[0]) + 1).toString() + " Day",
        dates: `${startDate} to ${endDate}`,
        date: item[0],
        month: getMonthFromDate(item[0]),
        block: item[1],
        // station: areaMap[item[2]] || item[2], // masked GSS station name
        area: areaMap[item[2]] || item[2], // divisional_headquarter → Group By Area
        key_name: item[3],
        district: districtMap[item[4]] || item[4],
        division: divisionMap[item[5]] || item[5],
        discom: discomMap[item[6]] || item[6],
        mw,
        mwh: mw / 4,
        lu: mw / 400,
      };
    });

    return addRandomToNumberFields(mapped, randomValue);
  } catch (error) {
    console.error("Error fetching Rajasthan Discom Forecast data:", error);
    return [];
  }
}


export async function getRajasthanDiscomDrawlForecastData({
  startDate,
  endDate,
}: {
  startDate?: string;
  endDate?: string;
}): Promise<RajasthanDiscomDrawal[]> {
  try {
    const randomValue = generateRandomValue();
    const res = await buildHttpResponse({
      endPoint: `rajasthan/getRajasthanDiscomDrawalForecast?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });
    const response = toArray<any>(res);
    if (response.length === 0) return [];

    const uniqueDates = Array.from(new Set(response.map((item) => item[1])));
    const mapped = response.map((item) => {
      return {
        dateIndex: (uniqueDates.indexOf(item[1]) + 1).toString() + " Day",
        dates: `${startDate} to ${endDate}`,
        date: item[1],
        month: getMonthFromDate(item[1]),
        block: item[2],
        area: item[3],
        mw: item[4],
        lu: item[4] / 400,
        discom: item[5],
        division: item[6],
        district: item[7],
        mwh: 0,
      };
    });

    const discoms: string[] = [];
    const area: string[] = [];
    for (let i = 0; i < mapped.length; i++) {
      const element = mapped[i].discom;
      const element2 = mapped[i].area;
      if (discoms.includes(element)) {
        continue;
      } else {
        discoms.push(element);
      }
      if (area.includes(element2)) {
        continue;
      } else {
        area.push(element2);
      }
    }

    const modifiedMapping = mapped.map((item) => {
      const discomIndex = discoms.indexOf(item.discom);
      const newDiscomName = `Discom ${discomIndex + 1}`;
      const areaIndex = area.indexOf(item.area);
      const newAreaName = `Area ${areaIndex + 1}`;
      return {
        ...item,
        discom: newDiscomName,
        area: newAreaName,
      };
    });

    return addRandomToNumberFields(modifiedMapping, randomValue);
  } catch (error) {
    console.error("Error fetching Rajasthan Discom Drawl data:", error);
    return [];
  }
}

export async function getRajasthanDailyPeak(
  startDate: string,
  endDate: string,
): Promise<any[]> {
  try {
    const randomValue = generateRandomValue();
    const response: any = await buildHttpResponse({
      endPoint: `rajasthan/getDailyPeakDemand?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const data = response?.data;
    if (!Array.isArray(data) || data.length === 0) return [];

    const uniqueDates = Array.from(new Set(data.map((item: any) => item.date)));

    const mapped = data.map((item: any) => {
      const morningData = item.formatted_data?.find(
        (fd: any) => fd.time_period === "Morning",
      );
      const eveningData = item.formatted_data?.find(
        (fd: any) => fd.time_period === "Evening",
      );

      return {
        ...item,
        dateIndex: `${uniqueDates.indexOf(item.date) + 1} Day`,
        dates: `${startDate} to ${endDate}`,
        month: getMonthFromDate(item.date),
        peak_mw: parseFloat(item.daily_peak_demand_value_mw?.toString() ?? "0"),
        average_mw: parseFloat(
          item.avg_daily_peak_demand_value_mw?.toString() ?? "0",
        ),
        morning_peak_mw: parseFloat(morningData?.max_demand?.toString() ?? "0"),
        morning_peak_slot: morningData?.max_demand_slot ?? "",
        evening_peak_mw: parseFloat(eveningData?.max_demand?.toString() ?? "0"),
        evening_peak_slot: eveningData?.max_demand_slot ?? "",
      };
    });

    return addRandomToNumberFields(mapped, randomValue);
  } catch (error) {
    console.error("Error fetching Rajasthan Peak data:", error);
    return [];
  }
}


export async function getRajasthanMonthlyPeakDiscom(
  startDate: string,
  endDate: string,
): Promise<any[]> {
  try {
    const randomValue = generateRandomValue();
    const response: any = await buildHttpResponse({
      endPoint: `rajasthan/getMonthlyPeakDemand?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const data = response?.data;
    if (!Array.isArray(data) || data.length === 0) return [];

    const mapped = data.map((item: any) => ({
      month: item.month,
      year: item.year,
      average_mw: parseFloat(item.average_mw?.toString() ?? "0"),
      peak_mw: parseFloat(item.peak_mw?.toString() ?? "0"),
      date_of_peak: item.date_of_peak,
      timeblock_of_peak: item.timeblock_of_peak,
      morning_peak_mw: parseFloat(item.morning_peak_mw?.toString() ?? "0"),
      date_of_morning_peak: item.date_of_morning_peak,
      timeblock_of_morning_peak: item.timeblock_of_morning_peak,
      evening_peak_mw: parseFloat(item.evening_peak_mw?.toString() ?? "0"),
      date_of_evening_peak: item.date_of_evening_peak,
      timeblock_of_evening_peak: item.timeblock_of_evening_peak,
    }));

    return addRandomToNumberFields(mapped, randomValue);
  } catch (error) {
    console.error("Error fetching Rajasthan Monthly Peak data:", error);
    return [];
  }
}


export async function getDailyDiscomDrawalSchedule(
  startDate: string,
  endDate: string,
): Promise<any[]> {
  try {
    const randomValue = generateRandomValue();
    const response: any = await buildHttpResponse({
      endPoint: `rajasthan/getDailyDiscomDrawalSchedule?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const data: any[] = response?.data ?? [];
    if (!Array.isArray(data) || data.length === 0) return [];

    const uniqueDates = Array.from(new Set(data.map((item) => item.date)));
    const parseNum = (val: any) => parseFloat(val?.toString() ?? "0");

    const discomMap: Record<string, string> = {
      JDVVNL: "Discom 3",
      JVVNL: "Discom 1",
      AVVNL: "Discom 2",
    };

    const mapped = data.map((item) => {
      const morningData = item.formatted_data?.find(
        (fd: any) => fd.time_period === "Morning",
      );
      const eveningData = item.formatted_data?.find(
        (fd: any) => fd.time_period === "Evening",
      );

      const daily_peak_drawal_mw = Math.max(
        parseNum(morningData?.max_drawal),
        parseNum(eveningData?.max_drawal),
      );
      const daily_peak_schedule_mw = Math.max(
        parseNum(morningData?.max_schedule),
        parseNum(eveningData?.max_schedule),
      );
      const avg_daily_drawal_mw =
        (parseNum(morningData?.avg_drawal) +
          parseNum(eveningData?.avg_drawal)) /
        2;
      const avg_daily_schedule_mw =
        (parseNum(morningData?.avg_schedule) +
          parseNum(eveningData?.avg_schedule)) /
        2;

      return {
        ...item,
        dateIndex: `${uniqueDates.indexOf(item.date) + 1} Day`,
        dates: `${startDate} to ${endDate}`,
        month: getMonthFromDate(item.date),
        date: item.date,
        // ✅ DEMO: anonymise discom name
        discom: discomMap[item.discom] || item.discom,
        daily_peak_drawal_mw,
        daily_peak_schedule_mw,
        avg_daily_drawal_mw,
        avg_daily_schedule_mw,
        peak_drawal_mw: daily_peak_drawal_mw,
        peak_schedule_mw: daily_peak_schedule_mw,
        average_drawal_mw: avg_daily_drawal_mw,
        average_schedule_mw: avg_daily_schedule_mw,
        morning_peak_drawal_mw: parseNum(morningData?.max_drawal),
        morning_peak_drawal_slot: morningData?.max_drawal_slot ?? "",
        morning_peak_schedule_mw: parseNum(morningData?.max_schedule),
        morning_peak_schedule_slot: morningData?.max_schedule_slot ?? "",
        evening_peak_drawal_mw: parseNum(eveningData?.max_drawal),
        evening_peak_drawal_slot: eveningData?.max_drawal_slot ?? "",
        evening_peak_schedule_mw: parseNum(eveningData?.max_schedule),
        evening_peak_schedule_slot: eveningData?.max_schedule_slot ?? "",
        formatted_data: item.formatted_data ?? [],
      };
    });

    return addRandomToNumberFields(mapped, randomValue);
  } catch (error) {
    console.error("Error fetching Rajasthan Discom drawal schedule:", error);
    return [];
  }
}



export async function getMonthlyDiscomDrawalSchedule(
  startDate: string,
  endDate: string,
): Promise<any[]> {
  try {
    const randomValue = generateRandomValue();
    const response: any = await buildHttpResponse({
      endPoint: `rajasthan/getMonthlyDiscomDrawalSchedule?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const data: any[] = response?.data ?? [];
    if (!Array.isArray(data) || data.length === 0) return [];

    const mapped = data.map((item: any) => ({
      month: item.month,
      year: item.year,
      // ✅ DEMO: anonymise discom name
      discom: discomMap[item.discom] || item.discom,
      average_drawal_mw: parseFloat(item.average_drawal_mw?.toString() || "0"),
      average_schedule_mw: parseFloat(
        item.average_schedule_mw?.toString() || "0",
      ),
      peak_drawal_mw: parseFloat(item.peak_drawal_mw?.toString() || "0"),
      date_of_peak_drawal: item.date_of_peak_drawal,
      timeblock_of_peak_drawal: item.timeblock_of_peak_drawal,
      peak_schedule_mw: parseFloat(item.peak_schedule_mw?.toString() || "0"),
      date_of_peak_schedule: item.date_of_peak_schedule,
      timeblock_of_peak_schedule: item.timeblock_of_peak_schedule,
      morning_peak_drawal_mw: parseFloat(
        item.morning_peak_drawal_mw?.toString() || "0",
      ),
      date_of_morning_peak_drawal: item.date_of_morning_peak_drawal,
      timeblock_of_morning_peak_drawal: item.timeblock_of_morning_peak_drawal,
      morning_peak_schedule_mw: parseFloat(
        item.morning_peak_schedule_mw?.toString() || "0",
      ),
      date_of_morning_peak_schedule: item.date_of_morning_peak_schedule,
      timeblock_of_morning_peak_schedule:
        item.timeblock_of_morning_peak_schedule,
      evening_peak_drawal_mw: parseFloat(
        item.evening_peak_drawal_mw?.toString() || "0",
      ),
      date_of_evening_peak_drawal: item.date_of_evening_peak_drawal,
      timeblock_of_evening_peak_drawal: item.timeblock_of_evening_peak_drawal,
      evening_peak_schedule_mw: parseFloat(
        item.evening_peak_schedule_mw?.toString() || "0",
      ),
      date_of_evening_peak_schedule: item.date_of_evening_peak_schedule,
      timeblock_of_evening_peak_schedule:
        item.timeblock_of_evening_peak_schedule,
    }));

    return addRandomToNumberFields(mapped, randomValue);
  } catch (error) {
    console.error("Error fetching Monthly Discom Drawal Schedule data:", error);
    return [];
  }
}


export async function getDailyTotalDrawalSchedule(
  startDate: string,
  endDate: string,
  sourcesParam: string = "",
): Promise<any[]> {
  try {
    const randomValue = generateRandomValue();
    const response: any = await buildHttpResponse({
      endPoint: `rajasthan/getDailyTotalDrawalSchedule?start_date=${startDate}&end_date=${endDate}${sourcesParam}`,
      method: HttpMethod.GET,
    });

    // ✅ Unwrap { success, count, data } envelope
    const data: any[] = Array.isArray(response)
      ? response
      : (response?.data ?? []);
    if (data.length === 0) return [];

    // ✅ DEMO: anonymise discom field if present
    const mapped = data.map((item: any) => ({
      ...item,
      ...(item.discom ? { discom: discomMap[item.discom] || item.discom } : {}),
    }));

    return addRandomToNumberFields(mapped, randomValue);
  } catch (error) {
    console.error("Error fetching Daily Total Drawal Schedule data:", error);
    return [];
  }
}


export async function getMonthlyTotalDrawalSchedule(
  startDate: string,
  endDate: string,
  sourcesParam: string = "",
): Promise<any[]> {
  try {
    const randomValue = generateRandomValue();
    const response: any = await buildHttpResponse({
      endPoint: `rajasthan/getMonthlyTotalDrawalSchedule?start_date=${startDate}&end_date=${endDate}${sourcesParam}`,
      method: HttpMethod.GET,
    });

    // ✅ Unwrap { success, count, data } envelope
    const data: any[] = Array.isArray(response)
      ? response
      : (response?.data ?? []);
    if (data.length === 0) return [];

    // ✅ DEMO: anonymise discom field if present
    const mapped = data.map((item: any) => ({
      ...item,
      ...(item.discom ? { discom: discomMap[item.discom] || item.discom } : {}),
    }));

    return addRandomToNumberFields(mapped, randomValue);
  } catch (error) {
    console.error("Error fetching Monthly Total Drawal Schedule data:", error);
    return [];
  }
}





export async function fetchRvpnData(
  startDate: string,
  endDate: string,
): Promise<any[]> {
  try {
    const randomValue = generateRandomValue();

    const res = await buildHttpResponse({
      endPoint: `rajasthan/getRajasthanNreb?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const columns: string[] = res.columns ?? [];
    const rows: any[][] = res.data ?? [];

    if (rows.length === 0) return [];

    // Map columnar arrays → objects
    const response = rows.map((row) => {
      const obj: Record<string, any> = {};
      columns.forEach((col, i) => {
        obj[col] = row[i];
      });
      return obj;
    });

    const uniqueDates = Array.from(new Set(response.map((item) => item.date)));

    const mapped = response.map((item) => ({
      ...item,
      date: item.date,
      block: item.slot, // slot → block
      slot_id: item.slot, // keep for compatibility
      drawal_mw: item.drawal_mw,
      drawal_mwh: item.drawal_mw / 4,
      drawal_lu: item.drawal_mw / 400,
      rvpnl_drawal_mw: item.drawal_mw,
      rvpnl_drawal_mwh: item.drawal_mw / 4,
      rvpnl_drawal_lu: item.drawal_mw / 400,
      total_mw: item.drawal_mw,
      total_mwh: item.drawal_mw / 4,
      total_lu: item.drawal_mw / 400,
      schedule_mw: 0,
      schedule_mwh: 0,
      schedule_lu: 0,
      load_shedding_mw: 0,
      load_shedding_mwh: 0,
      load_shedding_lu: 0,
      dateIndex: `${uniqueDates.indexOf(item.date) + 1} Day`,
      dates: `${startDate} to ${endDate}`,
      month: getMonthFromDate(item.date),
    }));

    return addRandomToNumberFields(mapped, randomValue);
  } catch (error) {
    console.error("Error fetching Rajasthan RVPN data:", error);
    return [];
  }
}


export async function getRajasthanDemandMetrics(
  startDate: string,
  endDate: string,
): Promise<any[]> {
  try {
    const randomValue = generateRandomValue();
    const res = await buildHttpResponse({
      endPoint: `rajasthan/getDemandMetrics?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const response: any[] = Array.isArray(res) ? res : (res?.data ?? []);
    if (response.length === 0) return [];

    const mapped = response.map((item: any) => ({
      slot: item.slot,
      time_string: item.time_string,
      min_demand: parseFloat(item.min_demand?.toString() || "0"),
      max_demand: parseFloat(item.max_demand?.toString() || "0"),
      avg_demand: parseFloat(item.avg_demand?.toString() || "0"),
      mode_demand_window_formula: parseFloat(
        item.mode_demand?.toString() || "0",
      ),
      date_of_min: item.date_of_min_demand,
      date_of_max: item.date_of_max_demand,
    }));

    return addRandomToNumberFields(mapped, randomValue);
  } catch (error) {
    console.error("Error fetching Rajasthan Demand Metrics data:", error);
    return [];
  }
}

export async function getDiscomDrawalScheduleMetrics(
  startDate: string,
  endDate: string,
): Promise<any[]> {
  try {
    const randomValue = generateRandomValue();
    const res = await buildHttpResponse({
      endPoint: `rajasthan/getDiscomDrawalScheduleMetrics?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const response: any[] = Array.isArray(res) ? res : (res?.data ?? []);
    if (response.length === 0) return [];

    const discomMap: Record<string, string> = {
      JDVVNL: "Discom 1",
      JVVNL: "Discom 2",
      AVVNL: "Discom 3",
    };

    const mapped = response.map((item: any) => ({
      ...item,
      discom: discomMap[item.discom] || item.discom,
      slot: item.slot,
      time_string: item.time_string,
      min_drawal: parseFloat(item.min_drawal?.toString() || "0"),
      max_drawal: parseFloat(item.max_drawal?.toString() || "0"),
      avg_drawal: parseFloat(item.avg_drawal?.toString() || "0"),
      mode_drawal_window_formula: parseFloat(
        item.mode_drawal?.toString() || "0",
      ),
      min_schedule: parseFloat(item.min_schedule?.toString() || "0"),
      max_schedule: parseFloat(item.max_schedule?.toString() || "0"),
      avg_schedule: parseFloat(item.avg_schedule?.toString() || "0"),
      mode_schedule_window_formula: parseFloat(
        item.mode_schedule?.toString() || "0",
      ),
      date_of_min_drawal: item.date_of_min_drawal,
      date_of_max_drawal: item.date_of_max_drawal,
      date_of_min_schedule: item.date_of_min_schedule,
      date_of_max_schedule: item.date_of_max_schedule,
    }));

    return addRandomToNumberFields(mapped, randomValue);
  } catch (error) {
    console.error("Error fetching Discom Drawal Schedule Metrics data:", error);
    return [];
  }
}



export async function getTotalDrawalScheduleMetrics(
  startDate: string,
  endDate: string,
  sourcesParam: string = "",
): Promise<any[]> {
  try {
    const randomValue = generateRandomValue();
    const res = await buildHttpResponse({
      endPoint: `rajasthan/getTotalDrawalScheduleMetrics?start_date=${startDate}&end_date=${endDate}${sourcesParam}`,
      method: HttpMethod.GET,
    });

    const response: any[] = Array.isArray(res) ? res : (res?.data ?? []);
    if (response.length === 0) return [];

    return addRandomToNumberFields(response, randomValue);
  } catch (error) {
    console.error("Error fetching Total Drawal Schedule Metrics data:", error);
    return [];
  }
}

function applyBlockReplacements(
  data: RajasthanDiscomData[],
): RajasthanDiscomData[] {
  const replacementMappings: {
    targetDate: string;
    discom: string;
    blocks: number[];
    sourceDate: string;
  }[] = [
      {
        targetDate: "2024-12-10",
        discom: "Jodhpur",
        blocks: [82],
        sourceDate: "2024-12-09",
      },
      {
        targetDate: "2024-12-10",
        discom: "Ajmer",
        blocks: [82],
        sourceDate: "2024-12-09",
      },
    ];

  const modifiedData = [...data]; // Create a copy to avoid modifying original data

  replacementMappings.forEach(({ targetDate, discom, blocks, sourceDate }) => {
    blocks.forEach((block) => {
      const sourceRecord = data.find(
        (record) =>
          record.date === sourceDate &&
          record.block === block &&
          record.discom === discom,
      );

      if (sourceRecord) {
        modifiedData.forEach((record) => {
          if (
            record.date === targetDate &&
            record.block === block &&
            record.discom === discom
          ) {
            record.schedule_mw = sourceRecord.schedule_mw; // Apply the replacement
          }
        });
      }
    });
  });

  return modifiedData;
}

function convertToRajasthanGenData(params: any[]): RajasthanGenData {
  return {
    date: params[0],
    month: getMonthFromDate(params[0]),
    state: "State 1",
    source: params[6],
    slot: params[1],
    station: params[2],
    onBarDS_mw: params[3],
    value_mw: params[3],
    value_mwh: params[3] / 4,
    onBarDc_mw: params[4],
    backdown_mw: params[5],
    onBarDS_mwh: params[3] / 4,
    onBarDc_mwh: params[4],
    backdown_mwh: params[5] / 4,
  };
}

export async function getRajasthanGenerationData(
  startDate: string,
  endDate: string,
): Promise<RajasthanGenData[]> {
  try {
    const randomValue = generateRandomValue();
    const response: any[][] = await buildHttpResponse({
      endPoint: `rajasthan/getGenerationData?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const mapped = response.map((item) => {
      return convertToRajasthanGenData(item);
    });

    const station: string[] = [];
    for (let i = 0; i < mapped.length; i++) {
      const element = mapped[i].station;
      if (station.includes(element)) {
        continue;
      } else {
        station.push(element);
      }
    }

    const modifiedMapping = mapped.map((item) => {
      const stationIndex = station.indexOf(item.station);

      const newStationName = `Station${stationIndex + 1}`;

      return {
        ...item,
        station: newStationName,
      };
    });

    return addRandomToNumberFields(modifiedMapping, randomValue);
  } catch (error) {
    console.error("Error fetching Rajasthan Generation data:", error);
    return [];
  }
}

export async function getRajasthanIsgsData({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}): Promise<RajasthanIsgs[]> {
  try {
    const randomValue = generateRandomValue();
    const res = await buildHttpResponse({
      endPoint: `rajasthan/getRajasthanIsgsData?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });
    const response = toArray<RajasthanIsgs>(res);
    if (response.length === 0) return [];

    const state: string[] = [];
    const utility: string[] = [];
    for (let i = 0; i < response.length; i++) {
      const element = response[i].state;
      const element2 = response[i].from_utility__name;
      if (state.includes(element)) {
        continue;
      } else {
        state.push(element);
      }
      if (utility.includes(element2)) {
        continue;
      } else {
        utility.push(element2);
      }
    }
    const mapped = response.map((item) => {
      return {
        ...item,
        value_mwh: item.value_mw / 4,
        station: item.from_utility__name,
        month: getMonthFromDate(item.date),
        state: item.state,
        from_utility__name: item.from_utility__name,
      };
    });
    return addRandomToNumberFields(mapped, randomValue);
  } catch (error) {
    console.error("Error fetching Rajasthan ISGS Data:", error);
    return [];
  }
}

export async function getRajasthanCollectiveData({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}): Promise<RajasthanCollective[]> {
  try {
    const randomValue = generateRandomValue();
    const response = await buildHttpResponse({
      endPoint: `rajasthan/getCollectiveData?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });
    return addRandomToNumberFields(response, randomValue);
  } catch (error) {
    console.error("Error fetching Rajasthan Collective Data:", error);
    return [];
  }
}

export async function getGnaTgnaData({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}): Promise<RajasthanGnaTgna[]> {
  try {
    const randomValue = generateRandomValue();
    const response = await buildHttpResponse({
      endPoint: `rajasthan/getGnaTgnaData?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    // Define fictional entity names for utilities
    const utilityNames = [
      "PowerGen Alpha",
      "EnergyX",
      "VoltaPower",
      "GridStar",
      "NeoEnergy",
      "PowerNorth",
      "SouthGrid",
      "EastPower",
      "WesternElectric",
      "CentralGen",
    ];

    // Define fictional state names
    const stateNames = [
      "StateA",
      "StateB",
      "StateC",
      "NorthState",
      "SouthState",
      "EastRegion",
      "WestRegion",
      "CentralZone",
    ];

    // Track which real name maps to which fictional name
    const fromUtilityMap = new Map<string, string>();
    const toUtilityMap = new Map<string, string>();
    const fromStateMap = new Map<string, string>();
    const toStateMap = new Map<string, string>();
    const entityStateMap = new Map<string, string>(); // Add map for entity states

    let fromUtilityCounter = 0;
    let toUtilityCounter = 0;
    let fromStateCounter = 0;
    let toStateCounter = 0;
    let entityStateCounter = 0; // Add counter for entity states

    const mapped: RajasthanGnaTgna[] = response.map(
      (item: RajasthanGnaTgna) => {
        // Get or create mapping for from_utility__name
        let fromUtilityName: string;
        if (fromUtilityMap.has(item.from_utility__name)) {
          fromUtilityName = fromUtilityMap.get(item.from_utility__name)!;
        } else {
          fromUtilityName =
            utilityNames[fromUtilityCounter % utilityNames.length];
          fromUtilityMap.set(item.from_utility__name, fromUtilityName);
          fromUtilityCounter++;
        }

        // Get or create mapping for to_utility__name
        let toUtilityName: string;
        if (toUtilityMap.has(item.to_utility__name)) {
          toUtilityName = toUtilityMap.get(item.to_utility__name)!;
        } else {
          toUtilityName = "Consumer" + (toUtilityCounter + 1);
          toUtilityMap.set(item.to_utility__name, toUtilityName);
          toUtilityCounter++;
        }

        // Handle from_state__name if it exists
        let fromStateName: string | null = null;
        if (item.from_state__name) {
          if (fromStateMap.has(item.from_state__name)) {
            fromStateName = fromStateMap.get(item.from_state__name)!;
          } else {
            fromStateName = stateNames[fromStateCounter % stateNames.length];
            fromStateMap.set(item.from_state__name, fromStateName);
            fromStateCounter++;
          }
        }

        // Handle to_state__name
        let toStateName: string;
        if (toStateMap.has(item.to_state__name)) {
          toStateName = toStateMap.get(item.to_state__name)!;
        } else {
          toStateName = stateNames[toStateCounter % stateNames.length];
          toStateMap.set(item.to_state__name, toStateName);
          toStateCounter++;
        }

        // Handle from_utility__entity_state__name if it exists
        let entityStateName: string | null = null;
        if (item.from_utility__entity_state__name) {
          if (entityStateMap.has(item.from_utility__entity_state__name)) {
            entityStateName = entityStateMap.get(
              item.from_utility__entity_state__name,
            )!;
          } else {
            entityStateName =
              stateNames[entityStateCounter % stateNames.length];
            entityStateMap.set(
              item.from_utility__entity_state__name,
              entityStateName,
            );
            entityStateCounter++;
          }
        }

        // Map IR link name
        const linkName = `Link-${fromStateName?.charAt(0) || "X"
          }${toStateName.charAt(0)}`;

        return {
          ...item,
          from_utility__name: fromUtilityName,
          to_utility__name: toUtilityName,
          from_state__name: fromStateName,
          to_state__name: toStateName,
          from_utility__entity_state__name: entityStateName, // Add anonymized entity state
          ir_link__name: linkName,
        };
      },
    );
    return addRandomToNumberFields(mapped, randomValue);
  } catch (error) {
    console.error("Error fetching GnaTgna data:", error);
    return [];
  }
}

export async function createRajProcurementRecord(
  formData: any,
): Promise<string> {
  try {
    const response = await buildHttpResponse({
      endPoint: `rajasthan/procurement/create`,
      method: HttpMethod.POST,
      body: formData,
    });
    return response.message;
  } catch (error) {
    console.error("Error fetching GnaTgna data:", error);
    return "Inserting error";
  }
}

export async function getRajFileTypes(): Promise<string[]> {
  try {
    const randomValue = generateRandomValue();
    const response = await buildHttpResponse({
      endPoint: "/rajasthan/get-file-types",
      method: HttpMethod.GET,
    });
    return addRandomToNumberFields(response, randomValue);
  } catch (error) {
    console.error("Error fetching Raj File Types:", error);
    return [];
  }
}

export async function uploadRajFile(formData: any): Promise<string> {
  const response = await buildMultipartHttpResponse({
    endPoint: "/rajasthan/upload-file", // Replace with your endpoint
    method: HttpMethod.POST,
    formData,
  });
  return response.message;
}

export async function getRajReData({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}): Promise<ReEnergyData[]> {
  try {
    const randomValue = generateRandomValue();

    const response: ReEnergyData[] = await buildHttpResponse({
      endPoint: `rajasthan/getRenewableGeneration?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const uniqueDates = Array.from(new Set(response.map((item) => item.date)));

    const mapped = response.map((item) => ({
      ...item,
      value_mwh: item.value_mw / 4,
      dateIndex: (uniqueDates.indexOf(item.date) + 1).toString() + " Day",
      dates: `${startDate} to ${endDate}`,
      month: getMonthFromDate(item.date),
      // ✅ These two fields are required by RajGenOverview when building reGenData
      energy_type: item.energy_type__name,
      time_block: item.slot,
    }));

    return addRandomToNumberFields(mapped, randomValue);
  } catch (error) {
    console.error("Error fetching Renewable Generation data:", error);
    return [];
  }
}

export async function getProcurementRecord(
  page: any,
  perpage: any,
): Promise<RajProcurementRecord[]> {
  try {
    const randomValue = generateRandomValue();
    const response = await fetch(
      `https://datahub.gna.energy/rajasthan_procurement?page=${page}&per_page=${perpage}`,
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return addRandomToNumberFields(data.data, randomValue);
  } catch (error) {
    console.error("Error fetching Procurement Record :", error);
    return [];
  }
}

export async function getRajOperatingPar(): Promise<RajGenParams[]> {
  try {
    const randomValue = generateRandomValue();
    const response = await buildHttpResponse({
      endPoint: `rajasthan/getRajOperatingPar`,
      method: HttpMethod.GET,
    });
    return addRandomToNumberFields(response, randomValue);
  } catch (error) {
    console.error("Error fetching Rajasthan Operating Parameters data:", error);
    return [];
  }
}

export async function getRajFiles(): Promise<RajasthanUploadFile[]> {
  try {
    const randomValue = generateRandomValue();
    const response = await buildHttpResponse({
      endPoint: `rajasthan/getRajUploadedFile`,
      method: HttpMethod.GET,
    });
    return addRandomToNumberFields(response, randomValue);
  } catch (error) {
    console.error("Error fetching Rajasthan Files data:", error);
    return [];
  }
}

export async function createThread(): Promise<{ thread_token: string }> {
  try {
    const response = await buildHttpResponse({
      endPoint: `dmrc/create_thread`,
      method: HttpMethod.POST,
    });
    return response;
  } catch (error) {
    console.error("Error creating thread:", error);
    return { thread_token: "" };
  }
}

export async function rajChatGptRes({
  query,
  thread_token,
}: {
  query: string;
  thread_token: string;
}): Promise<{ sender: String; content: String }> {
  const response = await buildHttpResponse({
    endPoint: `rajasthan/handeUserQuery?query=${query}&thread_token=${thread_token}`,
    method: HttpMethod.GET,
  });
  return response;
}

export async function uploadGridcoFile(formData: any): Promise<string> {
  const response = await buildMultipartHttpResponse({
    endPoint: "/gridco/upload_gridco_file", // Replace with your endpoint
    method: HttpMethod.POST,
    formData,
  });
  return response.message;
}

export async function createFileType(name: string): Promise<string> {
  const response = await buildHttpResponse({
    endPoint: "/gridco/create_file_type", // Replace with your endpoint
    method: HttpMethod.POST,
    body: { name },
  });
  return response.message;
}

export async function deleteFileType(name: string): Promise<string> {
  const response = await buildHttpResponse({
    endPoint: "/gridco/delete_file_type", // Replace with your endpoint
    method: HttpMethod.POST,
    body: { name },
  });
  return response.message;
}

export async function getGridcoUploadedFile(): Promise<string[]> {
  try {
    const randomValue = generateRandomValue();
    const response = await buildHttpResponse({
      endPoint: "/gridco/getGridcoUploadedFile",
      method: HttpMethod.GET,
    });
    return addRandomToNumberFields(response, randomValue);
  } catch (error) {
    console.error("Error fetching Gridco Uploaded File:", error);
    return [];
  }
}

export async function getGridcoFileTypes(): Promise<string[]> {
  try {
    const randomValue = generateRandomValue();
    const response = await buildHttpResponse({
      endPoint: "/gridco/get_gridco_file_types",
      method: HttpMethod.GET,
    });
    return addRandomToNumberFields(response, randomValue);
  } catch (error) {
    console.error("Error fetching Gridco File Types:", error);
    return [];
  }
}

export async function getRajGenProfileData({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}): Promise<RajGenProfile[]> {
  try {
    const randomValue = generateRandomValue();
    const response = await buildHttpResponse({
      endPoint: `/rajasthan/getGenProfileData?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });
    const res = await response;
    const discomMap: Record<string, string> = {
      JDVVNL: "Discom 1",
      JVVNL: "Discom 2",
      AVVNL: "Discom 3",
    };
    const mapped = res.map((item: any) => ({
      ...item,
      discom: discomMap[item.discom] || item.discom,
    }));
    return addRandomToNumberFields(mapped, randomValue);
  } catch (error) {
    console.error("Error fetching Raj Gen Profile Data:", error);
    return [];
  }
}

export async function getRajDeep({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}): Promise<RajProcurement[]> {
  try {
    const randomValue = generateRandomValue();
    const response = await buildHttpResponse({
      endPoint: `/rajasthan/getRajDeep?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });
    return addRandomToNumberFields(response, randomValue);
  } catch (error) {
    console.error("Error fetching Raj Deep data:", error);
    return [];
  }
}

export async function getRajIntraGen({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}): Promise<RajProcurement[]> {
  try {
    const randomValue = generateRandomValue();
    const response = await buildHttpResponse({
      endPoint: `/rajasthan/getRajIntraGen?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });
    return addRandomToNumberFields(response, randomValue);
  } catch (error) {
    console.error("Error fetching Raj IntraGen data:", error);
    return [];
  }
}

export async function getRajIsgs({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}): Promise<RajProcurement[]> {
  try {
    const randomValue = generateRandomValue();
    const response = await buildHttpResponse({
      endPoint: `/rajasthan/getRajIsgs?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });
    return addRandomToNumberFields(response, randomValue);
  } catch (error) {
    console.error("Error fetching Raj Isgs data:", error);
    return [];
  }
}

export async function getGridcoIntraGen({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}): Promise<IntrastateGen[]> {
  try {
    const randomValue = generateRandomValue();
    const response: IntrastateGen[] = await buildHttpResponse({
      endPoint: `gridco/get_intrastate_gen_data?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });
    const mapped = response.map((item) => {
      return {
        ...item,
        generator_type: titleCase(item.generator_type),
      };
    });
    return addRandomToNumberFields(mapped, randomValue);
  } catch (error) {
    console.error("Error fetching Gridco IntraGen data:", error);
    return [];
  }
}

export async function getRajTamData({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}): Promise<RajTamData[]> {
  try {
    const randomValue = generateRandomValue();
    const response: RajTamData[] = await buildHttpResponse({
      endPoint: `rajasthan/getRajNoarData?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });
    return addRandomToNumberFields(response, randomValue);
  } catch (error) {
    console.error("Error fetching Raj Tam data:", error);
    return [];
  }
}

export async function getRajNoar({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}): Promise<RajTamData[]> {
  try {
    const randomValue = generateRandomValue();
    const response: RajTamData[] = await buildHttpResponse({
      endPoint: `rajasthan/getRajNoarData?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });
    return addRandomToNumberFields(response, randomValue);
  } catch (error) {
    console.error("Error fetching Raj Noar data:", error);
    return [];
  }
}

export async function getGridcoIsgs({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}): Promise<GridoIsgs[]> {
  try {
    const randomValue = generateRandomValue();
    const response = await buildHttpResponse({
      endPoint: `gridco/get_grido_isgs_data?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });
    return addRandomToNumberFields(response, randomValue);
  } catch (error) {
    console.error("Error fetching Gridco ISGS data:", error);
    return [];
  }
}

export async function getGridcoRegen({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}): Promise<GridoIsgs[]> {
  try {
    const randomValue = generateRandomValue();
    const response = await buildHttpResponse({
      endPoint: `gridco/get_re_gen_data?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });
    return addRandomToNumberFields(response, randomValue);
  } catch (error) {
    console.error("Error fetching Gridco Regen data:", error);
    return [];
  }
}

export async function getRajBankingData({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}): Promise<RajBankingData[]> {
  try {
    const randomValue = generateRandomValue();
    const response: RajBankingData[] = await buildHttpResponse({
      endPoint: `rajasthan/getRajBankingData?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });
    const mapped = response.map((item) => {
      return {
        ...item,
        mwh: item.mwh,
      };
    });
    return addRandomToNumberFields(mapped, randomValue);
  } catch (error) {
    console.error("Error fetching Raj Banking data:", error);
    return [];
  }
}

// MOD Gridco START POST Session
export async function fetchgetModSession(startdate: string) {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/startSession?date=${startdate}`,
    method: HttpMethod.POST,
    body: {
      date: startdate,
    },
  });
  return response;
}

export async function getModDemoSession() {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/getSession`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}

export async function fetchModDemoArchive(startdate: string, session: number) {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/getPreviousSession?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
// MOD Stage data
export async function ModStagePost(stage: string, session: number) {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/postStage`,
    method: HttpMethod.POST,
    body: {
      stage: stage,
      session: session,
    },
    noCache: true,
  });
  return response;
}
export async function ModActivateSession(session: number) {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/activateSession`,
    method: HttpMethod.POST,
    body: {
      session: session,
    },
    noCache: true,
  });
  return response;
}

// MOD DAM default data
export async function fetchModDamDefault(startdate: string, session: number) {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/getDefaultData/dam?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function fetchModGDamDefault(startdate: string, session: number) {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/getDefaultData/gdam?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}

// MOD RTM default data
export async function fetchModRtmDefault(startdate: string, session: number) {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/getDefaultData/rtm?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}

// MOD Demand default data
export async function fetchModDemandDefault(
  startdate: string,
  session: number,
) {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/getDemandDefault?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}

// upload file POST DAM RTM DEMAND
export async function ModDamData(ModDamdata: any): Promise<ApiResponse> {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/postData/dam`,
    method: HttpMethod.POST,
    noCache: true,
    body: ModDamdata,
  });

  return response as ApiResponse;
}
export async function ModGDamData(ModGDamdata: any): Promise<ApiResponse> {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/postData/gdam`,
    method: HttpMethod.POST,
    noCache: true,
    body: ModGDamdata,
  });

  return response as ApiResponse;
}

export async function ModRtmData(ModRtmdata: any): Promise<ApiResponse> {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/postData/rtm`,
    method: HttpMethod.POST,
    noCache: true,
    body: ModRtmdata,
  });

  return response as ApiResponse;
}
export async function ModDemandData(ModDemanddata: any): Promise<ApiResponse> {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/postDemand`,
    method: HttpMethod.POST,
    noCache: true,
    body: ModDemanddata,
  });

  return response as ApiResponse;
}

export async function ModAggregatedLoadData(
  ModAggregatedData: any,
): Promise<ApiResponse> {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/postAggregatedLoadPrice`,
    method: HttpMethod.POST,
    body: ModAggregatedData,
  });

  return response as ApiResponse;
}

// MOD DAM fetch Data
export async function fetchModDamData(startdate: string, session: number) {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/getData/dam?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function fetchModGDamData(startdate: string, session: number) {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/getData/gdam?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function fetchModDemandData(startdate: string, session: number) {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/getDemand?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}

// MOD RTM fetch Data
export async function fetchModRtmData(startdate: string, session: number) {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/getData/rtm?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
// MOD TABLE fetch Data
export async function fetchModTableData(startdate: string, session: number) {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/getMOD?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function fetchModTableChartData(
  startdate: string,
  session: number,
) {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/getMODChart?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}

// BID final TABLE fetch Data
export async function fetchModBidTableData(startdate: string, session: number) {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/getBid?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function ModDemoUpdateBidData(
  ModUpdatedata: any,
): Promise<ApiResponse> {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/postBid`,
    method: HttpMethod.POST,
    noCache: true,
    body: ModUpdatedata,
  });

  return response as ApiResponse;
}
export async function getModDemoBidSimulationData(
  startdate: string,
  session: number,
) {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/getSimulatorBid?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
// post uploadGridcoModFile

export async function ModBulkUploadFile(formData: any): Promise<{
  automationMod_files: Array<{ file_url: string }>;
}> {
  const response = await buildMultipartHttpResponse({
    endPoint: "mod_demo/automation",
    method: HttpMethod.POST,
    formData,
  });
  return response;
}

export async function ModUpdateData(ModUpdatedata: any): Promise<ApiResponse> {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/postMOD`,
    method: HttpMethod.POST,
    noCache: true,
    body: ModUpdatedata,
  });

  return response as ApiResponse;
}

export async function CreateBidTable(ModUpdatedata: any): Promise<ApiResponse> {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/createBid`,
    method: HttpMethod.POST,
    noCache: true,
    body: ModUpdatedata,
  });

  return response as ApiResponse;
}
export async function fetchgetBidDownload(startdate: string, session: number) {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/getBidDownload?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });

  return response as ApiResponse;
}

//Simulator Summary
export async function fetchModSimulationSummary(
  startdate: string,
  session: number,
) {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/getSimulatorGenChart?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}

//Simulator Station
export async function fetchModSimulationStation(
  startdate: string,
  session: number,
) {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/getData4?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}

//Simulator Cost of Power
export async function fetchModSimulationCostPower(
  startdate: string,
  session: number,
) {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/getSimulatorCostPower?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
//Simulator Sale Revenue (IEX DAM)
export async function fetchModSimulationSaleRevenue(
  startdate: string,
  session: number,
) {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/getSimulatorSalesRevenue?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}

export async function fetchModSimulationTopBuyer(
  startdate: string,
  session: number,
) {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/getSimulatorGenPrice?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}

export async function fetchgetTemplateIPP(startdate: string, session: number) {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/downloadIPP?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });

  return response as ApiResponse;
}
export async function fetchgetTemplateISGS(startdate: string, session: number) {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/downloadISGS?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });

  return response as ApiResponse;
}
export async function fetchgetTemplateSGU(startdate: string, session: number) {
  const response = await buildHttpResponse({
    endPoint: `/mod_demo/downloadSGU?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });

  return response as ApiResponse;
}

export async function fetchLgbrTableData(startdate: string, session: number) {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/get-lgbr?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function fetchLgbrGetData(
  type: string | null,
  time_slot: number | null,
  startdate: string,
  session: number,
) {
  const response = await buildHttpResponse({
    endPoint: `mod_demo/get-lgbr-generator?date=${startdate}&session=${session}&type=${type}&time_slot=${time_slot}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}

// GNA/TGNA Optimizer Session

export async function fetchOptimizerSession(
  startDate: string,
  endDate: string,
) {
  const response = await buildHttpResponse({
    endPoint: `demo_gna/start-gna-session?start_date=${startDate}&end_date=${endDate}`,
    method: HttpMethod.POST,
    body: {
      start_date: startDate,
      end_date: endDate,
    },
  });
  return response;
}
export async function getGNAOptimizerSession() {
  const response = await buildHttpResponse({
    endPoint: `demo_gna/get-gna-session`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}

export async function fetchOptimizerSummary() {
  const response = await buildHttpResponse({
    endPoint: `demo_gna/generation-profile-slotwise`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function fetchOptimizerUtilisation() {
  const response = await buildHttpResponse({
    endPoint: `demo_gna/gna-utilisation-slotwise`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function fetchOptimizerMetrics() {
  const response = await buildHttpResponse({
    endPoint: `demo_gna/gna-utilisation-metrics`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function fetchOptimizerStatistics() {
  const response = await buildHttpResponse({
    endPoint: `demo_gna/gna-utilisation-statistics`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function fetchOptimizerGeneration() {
  const response = await buildHttpResponse({
    endPoint: `demo_gna/generation-profile-cummulative`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function fetchOptimizerSguGeneration() {
  const response = await buildHttpResponse({
    endPoint: `demo_gna/generation-profile`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}

export async function OptimizerSguUpdateData(
  Updatedata: any,
): Promise<ApiResponse> {
  const response = await buildHttpResponse({
    endPoint: `demo_gna/generation-profile`,
    method: HttpMethod.POST,
    body: Updatedata,
  });

  return response as ApiResponse;
}
export async function OptimizerCurrentUpdateData(
  Updatedata: any,
): Promise<ApiResponse> {
  const response = await buildHttpResponse({
    endPoint: `demo_gna/gna-parameters`,
    method: HttpMethod.POST,
    body: Updatedata,
  });

  return response as ApiResponse;
}

export async function fetchOptimizerCurrentOptimisation() {
  const response = await buildHttpResponse({
    endPoint: `demo_gna/gna-parameters`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function fetchDailyUtilisation() {
  const response = await buildHttpResponse({
    endPoint: `demo_gna/gna-utilisation-daily`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function fetchMonthlyUtilisation() {
  const response = await buildHttpResponse({
    endPoint: `demo_gna/gna-utilisation-monthly`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}

export async function OptimizerGnaProceed() {
  const response = await buildHttpResponse({
    endPoint: `demo_gna/gna-proceed`,
    method: HttpMethod.POST,
  });

  return response as ApiResponse;
}
export async function fetchOptimalUtilisation() {
  const response = await buildHttpResponse({
    endPoint: `demo_gna/gna-optimal-utilisation-slotwise`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function fetchSimulatedUtilisation() {
  const response = await buildHttpResponse({
    endPoint: `demo_gna/gna-simulated-utilisation-slotwise`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function fetchMonthlysimulatedUtilisation() {
  const response = await buildHttpResponse({
    endPoint: `demo_gna/gna-simulated-utilisation-monthly`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function fetchDailysimulatedUtilisation() {
  const response = await buildHttpResponse({
    endPoint: `demo_gna/gna-simulated-utilisation-daily`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function fetchMonthlyOptimalUtilisation() {
  const response = await buildHttpResponse({
    endPoint: `demo_gna/gna-optimal-utilisation-monthly`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function fetchDailyOptimalUtilisation() {
  const response = await buildHttpResponse({
    endPoint: `demo_gna/gna-optimal-utilisation-daily`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}

// Procurement Optimizer Session

export async function fetchProcurementDemoSession(
  startDate: string,
  endDate: string,
) {
  const response = await buildHttpResponse({
    endPoint: `procurement_demo/start-session?start_date=${startDate}&end_date=${endDate}`,
    method: HttpMethod.POST,
    body: {
      start_date: startDate,
      end_date: endDate,
    },
  });
  return response;
}

export async function getProcurementDemoSession() {
  const response = await buildHttpResponse({
    endPoint: `procurement_demo/get-session`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}

export async function AddSurplusDeficitData(
  SurplusDeficitData: any,
): Promise<ApiResponse> {
  const response = await buildHttpResponse({
    endPoint: `procurement_demo/upload-deficit-data`,
    method: HttpMethod.POST,
    noCache: true,
    body: SurplusDeficitData,
  });

  return response as ApiResponse;
}
export async function AddTraderOffersData(
  TraderOffersData: any,
): Promise<ApiResponse> {
  const response = await buildHttpResponse({
    endPoint: `procurement_demo/add-trader-offers`,
    method: HttpMethod.POST,
    noCache: true,
    body: TraderOffersData,
  });

  return response as ApiResponse;
}
export async function UpdateTraderOffersData(
  TraderOffersData: any,
): Promise<ApiResponse> {
  const response = await buildHttpResponse({
    endPoint: `procurement_demo/update-trader-offers`,
    method: HttpMethod.POST,
    noCache: true,
    body: TraderOffersData,
  });

  return response as ApiResponse;
}
export async function UpdateTraderOffersToggleData(
  TraderOffersData: any,
): Promise<ApiResponse> {
  const response = await buildHttpResponse({
    endPoint: `procurement_demo/toggle-trader-offers`,
    method: HttpMethod.POST,
    noCache: true,
    body: TraderOffersData,
  });

  return response as ApiResponse;
}

export async function fetchTraderOffersData() {
  const response = await buildHttpResponse({
    endPoint: `procurement_demo/get-trader-offers`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function fetchOptimalTraderOffersData() {
  const response = await buildHttpResponse({
    endPoint: `procurement_demo/get-optimal-trader-offers`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function getStatusTraderOffersData() {
  const response = await buildHttpResponse({
    endPoint: `procurement_demo/get-status-power`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}

export async function StartSimulationProcurementData(
  TraderOffersData: any,
): Promise<ApiResponse> {
  const response = await buildHttpResponse({
    endPoint: `procurement_demo/start-simulation`,
    method: HttpMethod.POST,
    noCache: true,
    body: TraderOffersData,
  });

  return response as ApiResponse;
}

export async function fetchSimulationProcurementData() {
  const response = await buildHttpResponse({
    endPoint: `procurement_demo/get-detailed-chart`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function fetchOptimalProcurementData() {
  const response = await buildHttpResponse({
    endPoint: `procurement_demo/optimal-data`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function fetchProcurementSummaryData() {
  const response = await buildHttpResponse({
    endPoint: `procurement_demo/get-summary-chart`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function fetchOptimalProcurementcummulativeData() {
  const response = await buildHttpResponse({
    endPoint: `procurement_demo/optimal-data-cummulative`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function fetchProcurementSimulationMetricsData() {
  const response = await buildHttpResponse({
    endPoint: `procurement_demo/get-summary-metric`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function fetchProcurementMetrics() {
  const response = await buildHttpResponse({
    endPoint: `procurement_demo/simulation-data`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function fetchProcurementStatistics() {
  const response = await buildHttpResponse({
    endPoint: `procurement_demo/summary-metrics`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}

export async function getSurDefProfile({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}): Promise<RajGenProfile[]> {
  try {
    const randomValue = generateRandomValue();
    const res = await buildHttpResponse({
      endPoint: `rajasthan/getSurDefProfile?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });
    const response = toArray<any>(res);
    if (response.length === 0) return [];
    
    const randomizedData = addRandomToNumberFields(response, randomValue);
    return randomizedData;
  } catch (error) {
    console.error("Error fetching Rajasthan SurDef Profile data:", error);
    return [];
  }
}

export async function getRajasthanDrawalScheduleForecastMetrics(
  startDate: string,
  endDate: string,
): Promise<any[]> {
  try {
    const randomValue = generateRandomValue();
    const res = await buildHttpResponse({
      endPoint: `rajasthan/getDrawalScheduleForecastMetrics?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const response: any[] = Array.isArray(res) ? res : (res?.data ?? []);
    if (response.length === 0) return [];

    const mapped = response.map((item: any) => ({
      ...item,
      block: item.block,
      discom: item.discom,
      model: item.model,
      forecast_type: item.forecast_type,
      time_string: item.time_string,
      // Schedule metrics
      min_schedule: parseFloat(item.min_schedule?.toString() || "0"),
      max_schedule: parseFloat(item.max_schedule?.toString() || "0"),
      avg_schedule: parseFloat(item.avg_schedule?.toString() || "0"),
      mode_schedule_window_formula: parseFloat(
        item.mode_schedule_window_formula?.toString() || "0",
      ),
      date_of_min_schedule: item.date_of_min_schedule,
      date_of_max_schedule: item.date_of_max_schedule,
      // Drawal metrics
      min_drawal: parseFloat(item.min_drawal?.toString() || "0"),
      max_drawal: parseFloat(item.max_drawal?.toString() || "0"),
      avg_drawal: parseFloat(item.avg_drawal?.toString() || "0"),
      mode_drawal_window_formula: parseFloat(
        item.mode_drawal_window_formula?.toString() || "0",
      ),
      date_of_min_drawal: item.date_of_min_drawal,
      date_of_max_drawal: item.date_of_max_drawal,
    }));

    return addRandomToNumberFields(mapped, randomValue);
  } catch (error) {
    console.error("Error fetching Rajasthan forecast metrics:", error);
    return [];
  }
}


export async function getRajasthanDailyPeakForecast(
  startDate: string,
  endDate: string,
): Promise<any[]> {
  try {
    const randomValue = generateRandomValue();
    const response: any = await buildHttpResponse({
      endPoint: `rajasthan/getDailyDrawalScheduleForecast?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    if (!Array.isArray(response?.data)) return [];
    const mapped = response.data
      .filter((item: any) => item.discom === "Total")
      .map((item: any) => ({
        date: item.date,
        discom: discomMap[item.discom] || item.discom,
        model: item.model,
        forecast_type: item.forecast_type, // "T+1" | "T+2"
        avg_daily_schedule_mw: item.avg_daily_schedule_mw || 0,
        daily_peak_schedule_mw: item.daily_peak_schedule_mw || 0,
        avg_daily_drawal_mw: item.avg_daily_drawal_mw || 0,
        daily_peak_drawal_mw: item.daily_peak_drawal_mw || 0,
        formatted_data: item.formatted_data || [],
      }));

    return addRandomToNumberFields(mapped, randomValue);
  } catch (error) {
    console.error("Error fetching daily peak forecast:", error);
    return [];
  }
}


export async function getRajasthanMonthlyPeakForecast(
  startDate: string,
  endDate: string,
): Promise<any[]> {
  try {
    const randomValue = generateRandomValue();
    const response: any = await buildHttpResponse({
      endPoint: `rajasthan/getMonthlyDrawalScheduleForecast?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    if (!Array.isArray(response?.data)) return [];

    const mapped = response.data
      .filter((item: any) => item.discom === "Total")
      .map((item: any) => ({
        month: item.month,
        year: item.year,
        discom: discomMap[item.discom] || item.discom,
        model: item.model,
        forecast_type: item.forecast_type, // "T+1" | "T+2"
        average_schedule_mw: item.average_schedule_mw || 0,
        peak_schedule_mw: item.peak_schedule_mw || 0,
        date_of_peak_schedule: item.date_of_peak_schedule,
        block_of_peak_schedule: item.block_of_peak_schedule,
        morning_peak_schedule_mw: item.morning_peak_schedule_mw || 0,
        date_of_morning_peak_schedule: item.date_of_morning_peak_schedule,
        block_of_morning_peak_schedule: item.block_of_morning_peak_schedule,
        evening_peak_schedule_mw: item.evening_peak_schedule_mw || 0,
        date_of_evening_peak_schedule: item.date_of_evening_peak_schedule,
        block_of_evening_peak_schedule: item.block_of_evening_peak_schedule,
        average_drawal_mw: item.average_drawal_mw || 0,
        peak_drawal_mw: item.peak_drawal_mw || 0,
        date_of_peak_drawal: item.date_of_peak_drawal,
        block_of_peak_drawal: item.block_of_peak_drawal,
        morning_peak_drawal_mw: item.morning_peak_drawal_mw || 0,
        date_of_morning_peak_drawal: item.date_of_morning_peak_drawal,
        block_of_morning_peak_drawal: item.block_of_morning_peak_drawal,
        evening_peak_drawal_mw: item.evening_peak_drawal_mw || 0,
        date_of_evening_peak_drawal: item.date_of_evening_peak_drawal,
        block_of_evening_peak_drawal: item.block_of_evening_peak_drawal,
      }));

    return addRandomToNumberFields(mapped, randomValue);
  } catch (error) {
    console.error("Error fetching monthly peak forecast:", error);
    return [];
  }
}


export async function getrestrictedAreaGssLoadDaily(
  startDate: string,
  endDate: string,
): Promise<any[]> {
  try {
    const response: Promise<{
      success: boolean;
      message: string;
      min_date: string;
      max_date: string;
      count: number;
      data: any[];
    }> = await buildHttpResponse({
      endPoint: `rajasthan/gss-load-daily?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const res = await response;
    const data = res.data;

    // Get unique dates for indexing
    const uniqueDates = Array.from(new Set(data.map((item) => item.date)));

    // Create demo maps for display names
    const { discomMap, areaMap, districtMap } = createDemoMaps(data);

    return data.map((item) => {
      return {
        date: item.date,
        discom: discomMap[item.discom] || item.discom,
        divisional_headquarter:
          districtMap[item.divisional_headquarter] ||
          item.divisional_headquarter,
        district: districtMap[item.district] || item.district,
        area: areaMap[item.station] || item.station,
        dateIndex: (uniqueDates.indexOf(item.date) + 1).toString() + " Day",
        dates: `${startDate} to ${endDate}`,
        month: getMonthFromDate(item.date),
        avg_daily_load_mw: parseFloat(item.avg_daily_load_mw.toString()),
        daily_peak_load_mw: parseFloat(item.daily_peak_load_mw.toString()),

        // Evening peak values
        evening_peak_load_mw: parseFloat(
          item.formatted_data[0].max_load.toString(),
        ),
        evening_peak_load_slot: item.formatted_data[0].max_load_slot,
        evening_min_load_mw: parseFloat(
          item.formatted_data[0].min_load.toString(),
        ),
        evening_min_load_slot: item.formatted_data[0].min_load_slot,
        evening_avg_load_mw: parseFloat(
          item.formatted_data[0].avg_load.toString(),
        ),

        // Morning peak values
        morning_peak_load_mw: parseFloat(
          item.formatted_data[1].max_load.toString(),
        ),
        morning_peak_load_slot: item.formatted_data[1].max_load_slot,
        morning_min_load_mw: parseFloat(
          item.formatted_data[1].min_load.toString(),
        ),
        morning_min_load_slot: item.formatted_data[1].min_load_slot,
        morning_avg_load_mw: parseFloat(
          item.formatted_data[1].avg_load.toString(),
        ),

        formatted_data: [
          {
            time_period: item.formatted_data[0].time_period,
            max_load: parseFloat(item.formatted_data[0].max_load.toString()),
            min_load: parseFloat(item.formatted_data[0].min_load.toString()),
            avg_load: parseFloat(item.formatted_data[0].avg_load.toString()),
            max_load_slot: item.formatted_data[0].max_load_slot,
            min_load_slot: item.formatted_data[0].min_load_slot,
          },
          {
            time_period: item.formatted_data[1].time_period,
            max_load: parseFloat(item.formatted_data[1].max_load.toString()),
            min_load: parseFloat(item.formatted_data[1].min_load.toString()),
            avg_load: parseFloat(item.formatted_data[1].avg_load.toString()),
            max_load_slot: item.formatted_data[1].max_load_slot,
            min_load_slot: item.formatted_data[1].min_load_slot,
          },
        ],
      };
    });
  } catch (error) {
    console.error("Error fetching GSS Load Daily data:", error);
    return [];
  }
}

export async function getGssrestrictedAreaLoadMonthly(
  startDate: string,
  endDate: string,
): Promise<any[]> {
  try {
    const response: Promise<{
      success: boolean;
      message: string;
      min_date: string;
      max_date: string;
      count: number;
      data: any[];
    }> = await buildHttpResponse({
      endPoint: `rajasthan/gss-load-monthly?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const res = await response;
    const data = res.data;

    // Helper function to format month
    const getMonthWithYear = (month: number, year: number): string => {
      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const yearShort = year.toString().slice(-2);
      return `${monthNames[month - 1]}-${yearShort}`;
    };

    // Create demo maps for display names
    const { discomMap, areaMap, districtMap } = createDemoMaps(data);

    return data.map((item) => {
      return {
        // Add the formatted month field that the component expects
        month: getMonthWithYear(item.month, item.year),
        year: item.year,
        discom: discomMap[item.discom] || item.discom,
        divisional_headquarter:
          districtMap[item.divisional_headquarter] ||
          item.divisional_headquarter,
        district: districtMap[item.district] || item.district,
        area: areaMap[item.station] || item.station,
        dates: `${startDate} to ${endDate}`,

        // Rename fields to match daily data structure
        avg_daily_load_mw: parseFloat(item.average_load_mw.toString()),
        daily_peak_load_mw: parseFloat(item.peak_load_mw.toString()),
        date_of_peak_load: item.date_of_peak_load,
        timeblock_of_peak_load: item.timeblock_of_peak_load,

        // Morning peak load
        morning_peak_load_mw: parseFloat(item.morning_peak_load_mw.toString()),
        date_of_morning_peak_load: item.date_of_morning_peak_load,
        timeblock_of_morning_peak_load: item.timeblock_of_morning_peak_load,

        // Evening peak load
        evening_peak_load_mw: parseFloat(item.evening_peak_load_mw.toString()),
        date_of_evening_peak_load: item.date_of_evening_peak_load,
        timeblock_of_evening_peak_load: item.timeblock_of_evening_peak_load,
      };
    });
  } catch (error) {
    console.error("Error fetching GSS Load Monthly data:", error);
    return [];
  }
}

export async function getMonthlyRestrictedDistrictDiscomGssLoad(
  startDate: string,
  endDate: string,
): Promise<any[]> {
  try {
    const response: Promise<{
      success: boolean;
      message: string;
      min_date: string;
      max_date: string;
      count: number;
      data: any[];
    }> = await buildHttpResponse({
      endPoint: `rajasthan/gss-load-monthly-discom-district?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const res = await response;
    const data = res.data;

    // Create demo maps for display names
    const { discomMap, districtMap } = createDemoMaps(data);

    return data.map((item: any) => {
      return {
        month: item.month,
        year: item.year,
        discom: discomMap[item.discom] || item.discom,
        district: districtMap[item.district] || item.district,

        // Average load
        average_load_mw: parseFloat(item.average_load_mw?.toString() || "0"),

        // Overall peak load
        peak_load_mw: parseFloat(item.peak_load_mw?.toString() || "0"),
        date_of_peak_load: item.date_of_peak_load,
        timeblock_of_peak_load: item.timeblock_of_peak_load,

        // Morning peak load
        morning_peak_load_mw: parseFloat(
          item.morning_peak_load_mw?.toString() || "0",
        ),
        date_of_morning_peak_load: item.date_of_morning_peak_load,
        timeblock_of_morning_peak_load: item.timeblock_of_morning_peak_load,

        // Evening peak load
        evening_peak_load_mw: parseFloat(
          item.evening_peak_load_mw?.toString() || "0",
        ),
        date_of_evening_peak_load: item.date_of_evening_peak_load,
        timeblock_of_evening_peak_load: item.timeblock_of_evening_peak_load,
      };
    });
  } catch (error) {
    console.error(
      "Error fetching Monthly Discom District GSS Load data:",
      error,
    );
    return [];
  }
}
export async function getRestrictedGssLoadDailyDiscom(
  startDate: string,
  endDate: string,
): Promise<any[]> {
  try {
    const response = (await buildHttpResponse({
      endPoint: `rajasthan/gss-load-daily-discom?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    })) as any;

    const data: any[] = response.data;

    // Pre-compute unique dates once
    const uniqueDates = [...new Set(data.map((item) => item.date))];
    const dateIndexMap = new Map(
      uniqueDates.map((date, idx) => [date, `${idx + 1} Day`]),
    );
    const dateRange = `${startDate} to ${endDate}`;

    // Create demo maps for display names
    const { discomMap } = createDemoMaps(data);

    // Use a more efficient mapping approach
    return data.map((item) => {
      const eveningData = item.formatted_data[0];
      const morningData = item.formatted_data[1];

      return {
        date: item.date,
        discom: discomMap[item.discom] || item.discom,
        dateIndex: dateIndexMap.get(item.date),
        dates: dateRange,
        month: getMonthFromDate(item.date),
        avg_daily_load_mw: +item.avg_daily_load_mw,
        daily_peak_load_mw: +item.daily_peak_load_mw,

        // Evening peak values
        evening_peak_load_mw: +eveningData.max_load,
        evening_peak_load_slot: eveningData.max_load_slot,
        evening_min_load_mw: +eveningData.min_load,
        evening_min_load_slot: eveningData.min_load_slot,
        evening_avg_load_mw: +eveningData.avg_load,

        // Morning peak values
        morning_peak_load_mw: +morningData.max_load,
        morning_peak_load_slot: morningData.max_load_slot,
        morning_min_load_mw: +morningData.min_load,
        morning_min_load_slot: morningData.min_load_slot,
        morning_avg_load_mw: +morningData.avg_load,

        formatted_data: [
          {
            time_period: eveningData.time_period,
            max_load: +eveningData.max_load,
            min_load: +eveningData.min_load,
            avg_load: +eveningData.avg_load,
            max_load_slot: eveningData.max_load_slot,
            min_load_slot: eveningData.min_load_slot,
          },
          {
            time_period: morningData.time_period,
            max_load: +morningData.max_load,
            min_load: +morningData.min_load,
            avg_load: +morningData.avg_load,
            max_load_slot: morningData.max_load_slot,
            min_load_slot: morningData.min_load_slot,
          },
        ],
      };
    });
  } catch (error) {
    console.error("Error fetching GSS Load Daily Discom data:", error);
    return [];
  }
}

export async function getRestrictedMonthlyDiscomGssLoad(
  startDate: string,
  endDate: string,
): Promise<any[]> {
  try {
    const response: Promise<{
      success: boolean;
      message: string;
      min_date: string;
      max_date: string;
      count: number;
      data: any[];
    }> = await buildHttpResponse({
      endPoint: `rajasthan/gss-load-monthly-discom?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const res = await response;
    const data = res.data;

    // Create demo maps for display names
    const { discomMap } = createDemoMaps(data);

    return data.map((item: any) => {
      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const yearShort = item.year.toString().slice(-2);
      const monthStr = `${monthNames[item.month - 1]}-${yearShort}`;

      return {
        month: monthStr,
        year: item.year,
        discom: discomMap[item.discom] || item.discom,

        // Average load
        avg_daily_load_mw: parseFloat(item.average_load_mw?.toString() || "0"),

        // Overall peak load (used as daily_peak_load_mw)
        daily_peak_load_mw: parseFloat(item.peak_load_mw?.toString() || "0"),
        date_of_peak_load: item.date_of_peak_load,
        timeblock_of_peak_load: item.timeblock_of_peak_load,

        // Morning peak load
        morning_peak_load_mw: parseFloat(
          item.morning_peak_load_mw?.toString() || "0",
        ),
        date_of_morning_peak_load: item.date_of_morning_peak_load,
        timeblock_of_morning_peak_load: item.timeblock_of_morning_peak_load,

        // Evening peak load
        evening_peak_load_mw: parseFloat(
          item.evening_peak_load_mw?.toString() || "0",
        ),
        date_of_evening_peak_load: item.date_of_evening_peak_load,
        timeblock_of_evening_peak_load: item.timeblock_of_evening_peak_load,
      };
    });
  } catch (error) {
    console.error("Error fetching Monthly Discom GSS Load data:", error);
    return [];
  }
}

export async function getGssLoadRestrictedDistrictDailyDiscom(
  startDate: string,
  endDate: string,
): Promise<any[]> {
  try {
    const response: Promise<{
      success: boolean;
      message: string;
      min_date: string;
      max_date: string;
      count: number;
      data: any[];
    }> = await buildHttpResponse({
      endPoint: `rajasthan/gss-load-daily-discom-district?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const res = await response;
    const data = res.data;

    // Get unique dates for indexing
    const uniqueDates = Array.from(new Set(data.map((item) => item.date)));

    // Create demo maps for display names
    const { discomMap, districtMap } = createDemoMaps(data);

    return data.map((item) => {
      return {
        date: item.date,
        discom: discomMap[item.discom] || item.discom,
        district: districtMap[item.district] || item.district,
        dateIndex: (uniqueDates.indexOf(item.date) + 1).toString() + " Day",
        dates: `${startDate} to ${endDate}`,
        month: getMonthFromDate(item.date),
        avg_daily_load_mw: parseFloat(item.avg_daily_load_mw.toString()),
        daily_peak_load_mw: parseFloat(item.daily_peak_load_mw.toString()),

        // Evening peak values
        evening_peak_load_mw: parseFloat(
          item.formatted_data[1].max_load.toString(),
        ),
        evening_peak_load_slot: item.formatted_data[0].max_load_slot,
        evening_min_load_mw: parseFloat(
          item.formatted_data[1].min_load.toString(),
        ),
        evening_min_load_slot: item.formatted_data[0].min_load_slot,
        evening_avg_load_mw: parseFloat(
          item.formatted_data[1].avg_load.toString(),
        ),

        // Morning peak values
        morning_peak_load_mw: parseFloat(
          item.formatted_data[0].max_load.toString(),
        ),
        morning_peak_load_slot: item.formatted_data[1].max_load_slot,
        morning_min_load_mw: parseFloat(
          item.formatted_data[0].min_load.toString(),
        ),
        morning_min_load_slot: item.formatted_data[1].min_load_slot,
        morning_avg_load_mw: parseFloat(
          item.formatted_data[0].avg_load.toString(),
        ),

        formatted_data: [
          {
            time_period: item.formatted_data[1].time_period,
            max_load: parseFloat(item.formatted_data[1].max_load.toString()),
            min_load: parseFloat(item.formatted_data[1].min_load.toString()),
            avg_load: parseFloat(item.formatted_data[1].avg_load.toString()),
            max_load_slot: item.formatted_data[1].max_load_slot,
            min_load_slot: item.formatted_data[1].min_load_slot,
          },
          {
            time_period: item.formatted_data[0].time_period,
            max_load: parseFloat(item.formatted_data[0].max_load.toString()),
            min_load: parseFloat(item.formatted_data[0].min_load.toString()),
            avg_load: parseFloat(item.formatted_data[0].avg_load.toString()),
            max_load_slot: item.formatted_data[0].max_load_slot,
            min_load_slot: item.formatted_data[0].min_load_slot,
          },
        ],
      };
    });
  } catch (error) {
    console.error("Error fetching GSS Load Daily Discom District data:", error);
    return [];
  }
}
export async function getRajasthanGssRestrictedAreaSummaryMetrics(
  startDate: string,
  endDate: string,
): Promise<any[]> {
  try {
  

    const randomValue = generateRandomValue();

    const response = await buildHttpResponse({
      endPoint: `rajasthan/gss-load-slot-wise-metrics?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const res = response?.data ?? response;
  

    if (!res || !Array.isArray(res) || res.length === 0) {
      console.warn("[AREA API] No data returned from API");
      return [];
    }

    const { discomMap, areaMap, districtMap } = createDemoMaps(res);

    const mappedData = res.map((item: any) => ({
      slot: item.slot,
      discom: discomMap[item.discom] || item.discom,
      area: areaMap[item.station] || item.station,
      district: districtMap[item.district] || item.district,
      time_string: item.time_string,
      min_load: parseFloat(item.min_load?.toString() || "0"),
      date_of_min_load: item.date_of_min_load,
      max_load: parseFloat(item.max_load?.toString() || "0"),
      date_of_max_load: item.date_of_max_load,
      avg_load: parseFloat(item.avg_load?.toString() || "0"),
      mode_load: parseFloat(item.mode_load?.toString() || "0"),
      mode_load_class_dynamic: parseFloat(
        item.mode_load_class_dynamic?.toString() || "0",
      ),
      mode_load_class_static: parseFloat(
        item.mode_load_class_static?.toString() || "0",
      ),
      mode_load_formula: parseFloat(item.mode_load_formula?.toString() || "0"),
      mode_load_window_formula: parseFloat(
        item.mode_load_window_formula?.toString() || "0",
      ),
    }));

    return addRandomToNumberFields(mappedData, randomValue);
  } catch (error) {
    console.error("[AREA API] Error fetching data:", error);
    if (error instanceof Error) {
      console.error("[AREA API] Error message:", error.message);
      console.error("[AREA API] Error stack:", error.stack);
    }
    return [];
  }
}

export async function getRajasthanGssRestrictedSummaryMetricsDiscom(
  startDate: string,
  endDate: string,
): Promise<any[]> {
  try {
    const randomValue = generateRandomValue();

    const response = await buildHttpResponse({
      endPoint: `rajasthan/gss-load-slot-wise-metrics-discom?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const res = response?.data ?? response;
    if (!res || !Array.isArray(res) || res.length === 0) return [];

    const { discomMap } = createDemoMaps(res);

    const mappedData = res.map((item: any) => ({
      slot: item.slot,
      discom: discomMap[item.discom] || item.discom,
      time_string: item.time_string,
      min_load: parseFloat(item.min_load?.toString() || "0"),
      date_of_min_load: item.date_of_min_load,
      max_load: parseFloat(item.max_load?.toString() || "0"),
      date_of_max_load: item.date_of_max_load,
      avg_load: parseFloat(item.avg_load?.toString() || "0"),
      mode_load: parseFloat(item.mode_load?.toString() || "0"),
      mode_load_class_dynamic: parseFloat(
        item.mode_load_class_dynamic?.toString() || "0",
      ),
      mode_load_class_static: parseFloat(
        item.mode_load_class_static?.toString() || "0",
      ),
      mode_load_formula: parseFloat(item.mode_load_formula?.toString() || "0"),
      mode_load_window_formula: parseFloat(
        item.mode_load_window_formula?.toString() || "0",
      ),
    }));

    return addRandomToNumberFields(mappedData, randomValue);
  } catch (error) {
    console.error("[DISCOM API] Error fetching data:", error);
    if (error instanceof Error) {
      console.error("[DISCOM API] Error message:", error.message);
      console.error("[DISCOM API] Error stack:", error.stack);
    }
    return [];
  }
}



export async function getRajasthanGssLoadMetricsRestrictedDiscomDistrict(
  startDate: string,
  endDate: string,
): Promise<any[]> {
  try {
    const randomValue = generateRandomValue();

    const response = await buildHttpResponse({
      endPoint: `rajasthan/gss-load-metrics-discom-district?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const res = response?.data ?? response;
    if (!res || !Array.isArray(res) || res.length === 0) return [];

    const { discomMap, districtMap } = createDemoMaps(res);

    const mappedData = res.map((item: any) => ({
      slot: item.slot,
      discom: discomMap[item.discom] || item.discom,
      district: districtMap[item.district] || item.district,
      time_string: item.time_string,
      min_load: parseFloat(item.min_load?.toString() || "0"),
      date_of_min_load: item.date_of_min_load,
      max_load: parseFloat(item.max_load?.toString() || "0"),
      date_of_max_load: item.date_of_max_load,
      avg_load: parseFloat(item.avg_load?.toString() || "0"),
      mode_load: parseFloat(item.mode_load?.toString() || "0"),
      mode_load_class_dynamic: parseFloat(
        item.mode_load_class_dynamic?.toString() || "0",
      ),
      mode_load_class_static: parseFloat(
        item.mode_load_class_static?.toString() || "0",
      ),
      mode_load_formula: parseFloat(item.mode_load_formula?.toString() || "0"),
      mode_load_window_formula: parseFloat(
        item.mode_load_window_formula?.toString() || "0",
      ),
    }));

    return addRandomToNumberFields(mappedData, randomValue);
  } catch (error) {
    console.error("[DISTRICT API] Error fetching data:", error);
    if (error instanceof Error) {
      console.error("[DISTRICT API] Error message:", error.message);
      console.error("[DISTRICT API] Error stack:", error.stack);
    }
    return [];
  }
}


export async function getRestrictedGssLoadDailyTotal(
  startDate: string,
  endDate: string,
): Promise<any[]> {
  try {
    const response: Promise<{
      success: boolean;
      message: string;
      min_date: string;
      max_date: string;
      count: number;
      data: any[];
    }> = await buildHttpResponse({
      endPoint: `rajasthan/gss-load-daily-total?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const res = await response;
    const data = res.data;

    if (!data || data.length === 0) {
      return [];
    }

    // Get unique dates for indexing
    const uniqueDates = Array.from(new Set(data.map((item) => item.date)));

    return data.map((item) => {
      return {
        date: item.date,
        dateIndex: (uniqueDates.indexOf(item.date) + 1).toString() + " Day",
        dates: `${startDate} to ${endDate}`,
        month: getMonthFromDate(item.date),
        avg_daily_load_mw: parseFloat(item.avg_daily_load_mw.toString()),
        daily_peak_load_mw: parseFloat(item.daily_peak_load_mw.toString()),

        // evening  peak values
        evening_peak_load_mw: parseFloat(
          item.formatted_data[1].max_load.toString(),
        ),
        evening_peak_load_slot: item.formatted_data[1].max_load_slot,
        evening_min_load_mw: parseFloat(
          item.formatted_data[1].min_load.toString(),
        ),
        evening_min_load_slot: item.formatted_data[1].min_load_slot,
        evening_avg_load_mw: parseFloat(
          item.formatted_data[1].avg_load.toString(),
        ),

        // Moring peak values
        morning_peak_load_mw: parseFloat(
          item.formatted_data[0].max_load.toString(),
        ),
        morning_peak_load_slot: item.formatted_data[0].max_load_slot,
        morning_min_load_mw: parseFloat(
          item.formatted_data[0].min_load.toString(),
        ),
        morning_min_load_slot: item.formatted_data[0].min_load_slot,
        morning_avg_load_mw: parseFloat(
          item.formatted_data[0].avg_load.toString(),
        ),

        formatted_data: [
          {
            time_period: item.formatted_data[0].time_period,
            max_load: parseFloat(item.formatted_data[0].max_load.toString()),
            min_load: parseFloat(item.formatted_data[0].min_load.toString()),
            avg_load: parseFloat(item.formatted_data[0].avg_load.toString()),
            max_load_slot: item.formatted_data[0].max_load_slot,
            min_load_slot: item.formatted_data[0].min_load_slot,
          },
          {
            time_period: item.formatted_data[1].time_period,
            max_load: parseFloat(item.formatted_data[1].max_load.toString()),
            min_load: parseFloat(item.formatted_data[1].min_load.toString()),
            avg_load: parseFloat(item.formatted_data[1].avg_load.toString()),
            max_load_slot: item.formatted_data[1].max_load_slot,
            min_load_slot: item.formatted_data[1].min_load_slot,
          },
        ],
      };
    });
  } catch (error) {
    console.error("[DAILY TOTAL API] Error fetching data:", error);

    if (error instanceof Error) {
      console.error("[DAILY TOTAL API] Error message:", error.message);
      console.error("[DAILY TOTAL API] Error stack:", error.stack);
    }

    return [];
  }
}

export async function getRestrictedGssLoadMonthlyTotal(
  startDate: string,
  endDate: string,
): Promise<any[]> {
  try {
    const response: Promise<{
      success: boolean;
      message: string;
      min_date: string;
      max_date: string;
      count: number;
      data: any[];
    }> = await buildHttpResponse({
      endPoint: `rajasthan/gss-load-monthly-total?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const res = await response;
    const data = res.data;

    if (!data || data.length === 0) {
      return [];
    }

    // Helper function to format month
    const getMonthWithYear = (month: number, year: number): string => {
      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const yearShort = year.toString().slice(-2);
      return `${monthNames[month - 1]}-${yearShort}`;
    };

    return data.map((item) => {
      return {
        // Add the formatted month field that the component expects
        month: getMonthWithYear(item.month, item.year),
        year: item.year,
        dates: `${startDate} to ${endDate}`,

        // Rename fields to match daily data structure
        avg_daily_load_mw: parseFloat(item.average_load_mw.toString()),
        daily_peak_load_mw: parseFloat(item.peak_load_mw.toString()),
        date_of_peak_load: item.date_of_peak_load,
        timeblock_of_peak_load: item.timeblock_of_peak_load,

        // Morning peak load
        morning_peak_load_mw: parseFloat(item.morning_peak_load_mw.toString()),
        date_of_morning_peak_load: item.date_of_morning_peak_load,
        timeblock_of_morning_peak_load: item.timeblock_of_morning_peak_load,

        // Evening peak load
        evening_peak_load_mw: parseFloat(item.evening_peak_load_mw.toString()),
        date_of_evening_peak_load: item.date_of_evening_peak_load,
        timeblock_of_evening_peak_load: item.timeblock_of_evening_peak_load,
      };
    });
  } catch (error) {
    console.error("[MONTHLY TOTAL API] Error fetching data:", error);

    if (error instanceof Error) {
      console.error("[MONTHLY TOTAL API] Error message:", error.message);
      console.error("[MONTHLY TOTAL API] Error stack:", error.stack);
    }

    return [];
  }
}

export async function getRajasthanGssRestrictedSummaryMetricsTotal(
  startDate: string,
  endDate: string,
): Promise<any[]> {
  try {
    const randomValue = generateRandomValue();

    const res = await buildHttpResponse({
      endPoint: `rajasthan/gss-load-slot-wise-metrics-total?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    if (!res) return [];

    // API returns { success, count, data: [...] }
    const rows: any[] = Array.isArray(res) ? res : (res.data ?? []);
    if (rows.length === 0) return [];

    // ▶ DEMO — no masking needed for total (no discom/area fields)
    const mappedData = rows.map((item: any) => ({
      slot: item.slot,
      time_string: item.time_string,
      min_load: parseFloat(item.min_load?.toString() || "0"),
      date_of_min_load: item.date_of_min_load,
      max_load: parseFloat(item.max_load?.toString() || "0"),
      date_of_max_load: item.date_of_max_load,
      avg_load: parseFloat(item.avg_load?.toString() || "0"),
      mode_load: parseFloat(item.mode_load?.toString() || "0"),
      mode_load_class_dynamic: parseFloat(
        item.mode_load_class_dynamic?.toString() || "0",
      ),
      mode_load_class_static: parseFloat(
        item.mode_load_class_static?.toString() || "0",
      ),
      mode_load_formula: parseFloat(item.mode_load_formula?.toString() || "0"),
      mode_load_window_formula: parseFloat(
        item.mode_load_window_formula?.toString() || "0",
      ),
    }));

    return addRandomToNumberFields(mappedData, randomValue);
  } catch (error) {
    console.error("[TOTAL API] Error fetching data:", error);
    if (error instanceof Error) {
      console.error("[TOTAL API] Error message:", error.message);
      console.error("[TOTAL API] Error stack:", error.stack);
    }
    return [];
  }
}


export async function getRajasthanSurplusDeficitMetrics(
  startDate: string,
  endDate: string,
): Promise<any[]> {
  try {
    const response = await buildHttpResponse({
      endPoint: `rajasthan/surplus-deficit-metrics?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    // API returns { success, count, data: [...] }
    const res = response?.data;

    if (!Array.isArray(res) || res.length === 0) {
      console.warn("[SURPLUS-DEFICIT API] No data returned");
      return [];
    }


    return res.map((item: any) => ({
      slot: item.time_block,

      // Drawal metrics
      min_drawal: parseFloat(item.min_drawal ?? 0),
      date_of_min_drawal: item.date_of_min_drawal,
      max_drawal: parseFloat(item.max_drawal ?? 0),
      date_of_max_drawal: item.date_of_max_drawal,
      avg_drawal: parseFloat(item.avg_drawal ?? 0),
      mode_drawal: parseFloat(item.mode_drawal ?? 0),

      // Total Generation + Procurement metrics
      min_total_gen_proc: parseFloat(item.min_total_gen_proc ?? 0),
      date_of_min_total_gen_proc: item.date_of_min_total_gen_proc,
      max_total_gen_proc: parseFloat(item.max_total_gen_proc ?? 0),
      date_of_max_total_gen_proc: item.date_of_max_total_gen_proc,
      avg_total_gen_proc: parseFloat(item.avg_total_gen_proc ?? 0),
      mode_total_gen_proc: parseFloat(item.mode_total_gen_proc ?? 0),

      // Surplus / Deficit metrics
      min_surplus_deficit: parseFloat(item.min_surplus_deficit ?? 0),
      date_of_min_surplus_deficit: item.date_of_min_surplus_deficit,
      max_surplus_deficit: parseFloat(item.max_surplus_deficit ?? 0),
      date_of_max_surplus_deficit: item.date_of_max_surplus_deficit,
      avg_surplus_deficit: parseFloat(item.avg_surplus_deficit ?? 0),
      mode_surplus_deficit: parseFloat(item.mode_surplus_deficit ?? 0),

      // Surplus / Deficit percentage metrics
      min_surplus_deficit_pct: parseFloat(item.min_surplus_deficit_pct ?? 0),
      date_of_min_surplus_deficit_pct: item.date_of_min_surplus_deficit_pct,
      max_surplus_deficit_pct: parseFloat(item.max_surplus_deficit_pct ?? 0),
      date_of_max_surplus_deficit_pct: item.date_of_max_surplus_deficit_pct,
      avg_surplus_deficit_pct: parseFloat(item.avg_surplus_deficit_pct ?? 0),
      mode_surplus_deficit_pct: parseFloat(item.mode_surplus_deficit_pct ?? 0),
    }));
  } catch (error) {
    console.error("[SURPLUS-DEFICIT API] Error fetching data:", error);
    if (error instanceof Error) {
      console.error("[SURPLUS-DEFICIT API] Message:", error.message);
      console.error("[SURPLUS-DEFICIT API] Stack:", error.stack);
    }
    return [];
  }
}

export async function getRajasthanMonthlySurplusDeficit(
  startDate: string,
  endDate: string,
): Promise<any[]> {
  try {
    const randomValue = generateRandomValue();
    const response: Promise<{ data: any[] }> = await buildHttpResponse({
      endPoint: `rajasthan/surplus-deficit-monthly?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const res = await response;
    const data = res.data;

    const mappedData = data.map((item: any) => {
      return {
        year: item.year,
        month: item.month,

        // Drawal metrics
        average_drawal_mw: parseFloat(
          item.average_drawal_mw?.toString() || "0",
        ),
        peak_drawal_mw: parseFloat(item.peak_drawal_mw?.toString() || "0"),
        date_of_peak_drawal: item.date_of_peak_drawal,
        block_of_peak_drawal: item.block_of_peak_drawal,
        morning_peak_drawal_mw: parseFloat(
          item.morning_peak_drawal_mw?.toString() || "0",
        ),
        date_of_morning_peak_drawal: item.date_of_morning_peak_drawal,
        block_of_morning_peak_drawal: item.block_of_morning_peak_drawal,
        evening_peak_drawal_mw: parseFloat(
          item.evening_peak_drawal_mw?.toString() || "0",
        ),
        date_of_evening_peak_drawal: item.date_of_evening_peak_drawal,
        block_of_evening_peak_drawal: item.block_of_evening_peak_drawal,

        // Generation/Procurement metrics
        average_total_gen_proc_mw: parseFloat(
          item.average_total_gen_proc_mw?.toString() || "0",
        ),
        peak_total_gen_proc_mw: parseFloat(
          item.peak_total_gen_proc_mw?.toString() || "0",
        ),
        date_of_peak_total_gen_proc: item.date_of_peak_total_gen_proc,
        block_of_peak_total_gen_proc: item.block_of_peak_total_gen_proc,
        morning_peak_total_gen_proc_mw: parseFloat(
          item.morning_peak_total_gen_proc_mw?.toString() || "0",
        ),
        date_of_morning_peak_total_gen_proc:
          item.date_of_morning_peak_total_gen_proc,
        block_of_morning_peak_total_gen_proc:
          item.block_of_morning_peak_total_gen_proc,
        evening_peak_total_gen_proc_mw: parseFloat(
          item.evening_peak_total_gen_proc_mw?.toString() || "0",
        ),
        date_of_evening_peak_total_gen_proc:
          item.date_of_evening_peak_total_gen_proc,
        block_of_evening_peak_total_gen_proc:
          item.block_of_evening_peak_total_gen_proc,

        // Surplus/Deficit metrics
        average_surplus_deficit_mw: parseFloat(
          item.average_surplus_deficit_mw?.toString() || "0",
        ),
        peak_surplus_deficit_mw: parseFloat(
          item.peak_surplus_deficit_mw?.toString() || "0",
        ),
        date_of_peak_surplus_deficit: item.date_of_peak_surplus_deficit,
        block_of_peak_surplus_deficit: item.block_of_peak_surplus_deficit,
        morning_peak_surplus_deficit_mw: parseFloat(
          item.morning_peak_surplus_deficit_mw?.toString() || "0",
        ),
        date_of_morning_peak_surplus_deficit:
          item.date_of_morning_peak_surplus_deficit,
        block_of_morning_peak_surplus_deficit:
          item.block_of_morning_peak_surplus_deficit,
        evening_peak_surplus_deficit_mw: parseFloat(
          item.evening_peak_surplus_deficit_mw?.toString() || "0",
        ),
        date_of_evening_peak_surplus_deficit:
          item.date_of_evening_peak_surplus_deficit,
        block_of_evening_peak_surplus_deficit:
          item.block_of_evening_peak_surplus_deficit,

        // Percentage metrics
        average_surplus_deficit_pct: parseFloat(
          item.average_surplus_deficit_pct?.toString() || "0",
        ),
        peak_surplus_deficit_pct: parseFloat(
          item.peak_surplus_deficit_pct?.toString() || "0",
        ),
        date_of_peak_surplus_deficit_pct: item.date_of_peak_surplus_deficit_pct,
        block_of_peak_surplus_deficit_pct:
          item.block_of_peak_surplus_deficit_pct,
        morning_peak_surplus_deficit_pct: parseFloat(
          item.morning_peak_surplus_deficit_pct?.toString() || "0",
        ),
        date_of_morning_peak_surplus_deficit_pct:
          item.date_of_morning_peak_surplus_deficit_pct,
        block_of_morning_peak_surplus_deficit_pct:
          item.block_of_morning_peak_surplus_deficit_pct,
        evening_peak_surplus_deficit_pct: parseFloat(
          item.evening_peak_surplus_deficit_pct?.toString() || "0",
        ),
        date_of_evening_peak_surplus_deficit_pct:
          item.date_of_evening_peak_surplus_deficit_pct,
        block_of_evening_peak_surplus_deficit_pct:
          item.block_of_evening_peak_surplus_deficit_pct,
      };
    });
    return addRandomToNumberFields(mappedData, randomValue);
  } catch (error) {
    console.error(
      "Error fetching Rajasthan Monthly Surplus/Deficit data:",
      error,
    );
    return [];
  }
}

export async function getRajasthanDailySurplusDeficit(
  startDate: string,
  endDate: string,
): Promise<any[]> {
  try {
    const randomValue = generateRandomValue();
    const response: Promise<{ data: any[] }> = await buildHttpResponse({
      endPoint: `rajasthan/surplus-deficit-daily?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const res = await response;
    const data = res.data;

    const mappedData = data.map((item: any) => {
      return {
        date: item.date,

        // Daily average metrics
        avg_daily_drawal_mw: parseFloat(
          item.avg_daily_drawal_mw?.toString() || "0",
        ),
        daily_peak_drawal_mw: parseFloat(
          item.daily_peak_drawal_mw?.toString() || "0",
        ),
        avg_daily_total_gen_proc_mw: parseFloat(
          item.avg_daily_total_gen_proc_mw?.toString() || "0",
        ),
        daily_peak_total_gen_proc_mw: parseFloat(
          item.daily_peak_total_gen_proc_mw?.toString() || "0",
        ),
        avg_daily_surplus_deficit_mw: parseFloat(
          item.avg_daily_surplus_deficit_mw?.toString() || "0",
        ),
        daily_peak_surplus_deficit_mw: parseFloat(
          item.daily_peak_surplus_deficit_mw?.toString() || "0",
        ),
        avg_daily_surplus_deficit_pct: parseFloat(
          item.avg_daily_surplus_deficit_pct?.toString() || "0",
        ),
        daily_peak_surplus_deficit_pct: parseFloat(
          item.daily_peak_surplus_deficit_pct?.toString() || "0",
        ),

        // Formatted data by time period (Morning/Evening)
        formatted_data:
          item.formatted_data?.map((period: any) => ({
            time_period: period.time_period,

            // Drawal metrics
            avg_drawal: parseFloat(period.avg_drawal?.toString() || "0"),
            max_drawal: parseFloat(period.max_drawal?.toString() || "0"),
            min_drawal: parseFloat(period.min_drawal?.toString() || "0"),
            max_drawal_block: period.max_drawal_block,
            min_drawal_block: period.min_drawal_block,

            // Generation/Procurement metrics
            avg_total_gen_proc: parseFloat(
              period.avg_total_gen_proc?.toString() || "0",
            ),
            max_total_gen_proc: parseFloat(
              period.max_total_gen_proc?.toString() || "0",
            ),
            min_total_gen_proc: parseFloat(
              period.min_total_gen_proc?.toString() || "0",
            ),
            max_total_gen_proc_block: period.max_total_gen_proc_block,
            min_total_gen_proc_block: period.min_total_gen_proc_block,

            // Surplus/Deficit metrics
            avg_surplus_deficit: parseFloat(
              period.avg_surplus_deficit?.toString() || "0",
            ),
            max_surplus_deficit: parseFloat(
              period.max_surplus_deficit?.toString() || "0",
            ),
            min_surplus_deficit: parseFloat(
              period.min_surplus_deficit?.toString() || "0",
            ),
            max_surplus_deficit_block: period.max_surplus_deficit_block,
            min_surplus_deficit_block: period.min_surplus_deficit_block,

            // Percentage metrics
            avg_surplus_deficit_pct: parseFloat(
              period.avg_surplus_deficit_pct?.toString() || "0",
            ),
            max_surplus_deficit_pct: parseFloat(
              period.max_surplus_deficit_pct?.toString() || "0",
            ),
            min_surplus_deficit_pct: parseFloat(
              period.min_surplus_deficit_pct?.toString() || "0",
            ),
          })) || [],
      };
    });
    return addRandomToNumberFields(mappedData, randomValue);
  } catch (error) {
    console.error(
      "Error fetching Rajasthan Daily Surplus/Deficit data:",
      error,
    );
    return [];
  }
}

export async function getGenerationEltrixBackdownDailySourcePlant(
  startDate: string,
  endDate: string,
  generationSources?: string[],
  plantNames?: string[],
): Promise<any[]> {
  try {
    let queryParams = `start_date=${startDate}&end_date=${endDate}`;

    if (generationSources && generationSources.length > 0) {
      queryParams += `&generation_source=${generationSources.join(",")}`;
    }
    if (plantNames && plantNames.length > 0) {
      queryParams += `&plant_name=${plantNames.join(",")}`;
    }

    const response: Promise<{
      success: boolean;
      message: string;
      min_date: string;
      max_date: string;
      count: number;
      data: any[];
    }> = await buildHttpResponse({
      endPoint: `rajasthan/eltrix-backdown-daily-source-plant?${queryParams}`,
      method: HttpMethod.GET,
      noCache: true,
    });

    const res = await response;
    if (!res.success) {
      console.error("API returned unsuccessful response:", res.message);
      return [];
    }

    return res.data.map((item: any) => ({
      date: item.date,
      generation_source: item.generation_source,
      total_plants: item.total_plants,
      source_avg_daily_onbards_mw: parseFloat(
        item.source_avg_daily_onbards_mw?.toString() || "0",
      ),
      source_peak_daily_onbards_mw: parseFloat(
        item.source_peak_daily_onbards_mw?.toString() || "0",
      ),
      source_avg_daily_onbardc_mw: parseFloat(
        item.source_avg_daily_onbardc_mw?.toString() || "0",
      ),
      source_peak_daily_onbardc_mw: parseFloat(
        item.source_peak_daily_onbardc_mw?.toString() || "0",
      ),
      source_avg_daily_backdown_mw: parseFloat(
        item.source_avg_daily_backdown_mw?.toString() || "0",
      ),
      source_peak_daily_backdown_mw: parseFloat(
        item.source_peak_daily_backdown_mw?.toString() || "0",
      ),
      plants:
        item.plants?.map((plant: any) => ({
          date: plant.date,
          generation_source: plant.generation_source,
          plant_name: plant.plant_name,
          avg_daily_onbards_mw: parseFloat(
            plant.avg_daily_onbards_mw?.toString() || "0",
          ),
          daily_peak_onbards_mw: parseFloat(
            plant.daily_peak_onbards_mw?.toString() || "0",
          ),
          avg_daily_onbardc_mw: parseFloat(
            plant.avg_daily_onbardc_mw?.toString() || "0",
          ),
          daily_peak_onbardc_mw: parseFloat(
            plant.daily_peak_onbardc_mw?.toString() || "0",
          ),
          avg_daily_backdown_mw: parseFloat(
            plant.avg_daily_backdown_mw?.toString() || "0",
          ),
          daily_peak_backdown_mw: parseFloat(
            plant.daily_peak_backdown_mw?.toString() || "0",
          ),
          formatted_data:
            plant.formatted_data?.map((period: any) => ({
              time_period: period.time_period,
              avg_onbards: parseFloat(period.avg_onbards?.toString() || "0"),
              max_onbards: parseFloat(period.max_onbards?.toString() || "0"),
              min_onbards: parseFloat(period.min_onbards?.toString() || "0"),
              max_onbards_slot: period.max_onbards_slot,
              min_onbards_slot: period.min_onbards_slot,
              avg_onbardc: parseFloat(period.avg_onbardc?.toString() || "0"),
              max_onbardc: parseFloat(period.max_onbardc?.toString() || "0"),
              min_onbardc: parseFloat(period.min_onbardc?.toString() || "0"),
              max_onbardc_slot: period.max_onbardc_slot,
              min_onbardc_slot: period.min_onbardc_slot,
              avg_backdown: parseFloat(period.avg_backdown?.toString() || "0"),
              max_backdown: parseFloat(period.max_backdown?.toString() || "0"),
              min_backdown: parseFloat(period.min_backdown?.toString() || "0"),
              max_backdown_slot: period.max_backdown_slot,
              min_backdown_slot: period.min_backdown_slot,
            })) || [],
        })) || [],
    }));
  } catch (error) {
    console.error(
      "Error fetching Eltrix Backdown Daily Source/Plant data:",
      error,
    );
    return [];
  }
}

export async function getReGenerationDailySourceArea(
  startDate: string,
  endDate: string,
  energyTypes?: string[],
  areas?: string[],
): Promise<any[]> {
  try {
    let queryParams = `start_date=${startDate}&end_date=${endDate}`;

    if (energyTypes && energyTypes.length > 0) {
      queryParams += `&energy_type=${energyTypes.join(",")}`;
    }
    if (areas && areas.length > 0) {
      queryParams += `&area=${areas.join(",")}`;
    }

    const res = await buildHttpResponse({
      endPoint: `rajasthan/re-generation-daily-source-area?${queryParams}`,
      method: HttpMethod.GET,
      noCache: true,
    });

    const data = Array.isArray(res) ? res : res?.data || [];
    if (!Array.isArray(data)) {
      console.error("API did not return an array of data:", res);
      return [];
    }

    return data.map((item: any) => ({
      date: item.date,
      energy_type: item.energy_type,
      total_areas: item.total_areas,
      source_avg_daily_value_mw: parseFloat(
        item.source_avg_daily_value_mw?.toString() || "0",
      ),
      source_peak_daily_value_mw: parseFloat(
        item.source_peak_daily_value_mw?.toString() || "0",
      ),
      areas:
        item.areas?.map((area: any) => ({
          area: area.area,
          date: area.date,
          energy_type: area.energy_type,
          avg_daily_value_mw: parseFloat(
            area.avg_daily_value_mw?.toString() || "0",
          ),
          daily_peak_value_mw: parseFloat(
            area.daily_peak_value_mw?.toString() || "0",
          ),
          formatted_data:
            area.formatted_data?.map((period: any) => ({
              time_period: period.time_period,
              avg_value: parseFloat(period.avg_value?.toString() || "0"),
              max_value: parseFloat(period.max_value?.toString() || "0"),
              min_value: parseFloat(period.min_value?.toString() || "0"),
              max_value_slot: period.max_value_slot,
              min_value_slot: period.min_value_slot,
            })) || [],
        })) || [],
    }));
  } catch (error) {
    console.error(
      "Error fetching RE Generation Daily Source/Area data:",
      error,
    );
    return [];
  }
}
export async function getIsgsMetricsDailySourceState(
  startDate: string,
  endDate: string,
  generationSources?: string[],
  states?: string[], // accepts demo OR real names — converted internally
  utilities?: string[],
): Promise<any[]> {
  try {
    let queryParams = `start_date=${startDate}&end_date=${endDate}`;

    if (generationSources && generationSources.length > 0) {
      queryParams += `&generation_source=${generationSources
        .map((s) => encodeURIComponent(s.toLowerCase()))
        .join(",")}`;
    }
    if (states && states.length > 0) {
      queryParams += `&state=${states.map((s) => encodeURIComponent(s)).join(",")}`;
    }
    if (utilities && utilities.length > 0) {
      queryParams += `&utility=${utilities.map((u) => encodeURIComponent(u)).join(",")}`;
    }

    const res = await buildHttpResponse({
      endPoint: `rajasthan/isgs-daily-source-state-utility?${queryParams}`,
      method: HttpMethod.GET,
      noCache: true,
    });

    const data = Array.isArray(res) ? res : res?.data || [];
    if (!Array.isArray(data)) {
      console.error("API did not return an array of data:", res);
      return [];
    }

    return data.map((item: any) => ({
      date: item.date,
      generation_source: item.generation_source,
      total_states: item.total_states,
      total_utilities: parseFloat(item.total_utilities?.toString() || "0"),
      source_avg_daily_value_mw: parseFloat(
        item.source_avg_daily_value_mw?.toString() || "0",
      ),
      source_peak_daily_value_mw: parseFloat(
        item.source_peak_daily_value_mw?.toString() || "0",
      ),
      states:
        item.states?.map((state: any) => ({
          date: state.date,
          state_name: state.state_name,
          total_utilities: state.total_utilities,
          generation_source: state.generation_source,
          state_avg_daily_value_mw: parseFloat(
            state.state_avg_daily_value_mw?.toString() || "0",
          ),
          state_peak_daily_value_mw: parseFloat(
            state.state_peak_daily_value_mw?.toString() || "0",
          ),
          utilities:
            state.utilities?.map((utility: any) => ({
              date: utility.date,
              state_name: utility.state_name,
              utility_name: utility.utility_name,
              generation_source: utility.generation_source,
              avg_daily_value_mw: parseFloat(
                utility.avg_daily_value_mw?.toString() || "0",
              ),
              daily_peak_value_mw: parseFloat(
                utility.daily_peak_value_mw?.toString() || "0",
              ),
              formatted_data:
                utility.formatted_data?.map((period: any) => ({
                  time_period: period.time_period,
                  avg_value: parseFloat(period.avg_value?.toString() || "0"),
                  max_value: parseFloat(period.max_value?.toString() || "0"),
                  min_value: parseFloat(period.min_value?.toString() || "0"),
                  max_value_slot: period.max_value_slot,
                  min_value_slot: period.min_value_slot,
                })) || [],
            })) || [],
        })) || [],
    }));
  } catch (error) {
    console.error(
      "Error fetching ISGS Daily Metrics Source/State data:",
      error,
    );
    return [];
  }
}

export async function getGenerationEltrixBackdownMonthlySourcePlant(
  startDate: string,
  endDate: string,
  generationSources?: string[],
  plantNames?: string[],
): Promise<any[]> {
  try {
    let queryParams = `start_date=${startDate}&end_date=${endDate}`;

    if (generationSources && generationSources.length > 0) {
      queryParams += `&generation_source=${generationSources.join(",")}`;
    }
    if (plantNames && plantNames.length > 0) {
      queryParams += `&plant_name=${plantNames.join(",")}`;
    }

    const response: Promise<{
      success: boolean;
      message: string;
      min_date: string;
      max_date: string;
      count: number;
      data: any[];
    }> = await buildHttpResponse({
      endPoint: `rajasthan/eltrix-backdown-monthly-source-plant?${queryParams}`,
      method: HttpMethod.GET,
      noCache: true,
    });

    const res = await response;
    if (!res.success) {
      console.error("API returned unsuccessful response:", res.message);
      return [];
    }

    return res.data.map((item: any) => ({
      year: item.year,
      month: item.month,
      generation_source: item.generation_source,
      total_plants: item.total_plants,
      source_average_onbards_mw: parseFloat(
        item.source_average_onbards_mw?.toString() || "0",
      ),
      source_peak_onbards_mw: parseFloat(
        item.source_peak_onbards_mw?.toString() || "0",
      ),
      source_average_onbardc_mw: parseFloat(
        item.source_average_onbardc_mw?.toString() || "0",
      ),
      source_peak_onbardc_mw: parseFloat(
        item.source_peak_onbardc_mw?.toString() || "0",
      ),
      source_average_backdown_mw: parseFloat(
        item.source_average_backdown_mw?.toString() || "0",
      ),
      source_peak_backdown_mw: parseFloat(
        item.source_peak_backdown_mw?.toString() || "0",
      ),
      plants:
        item.plants?.map((plant: any) => ({
          year: plant.year,
          month: plant.month,
          generation_source: plant.generation_source,
          plant_name: plant.plant_name,
          average_onbards_mw: parseFloat(
            plant.average_onbards_mw?.toString() || "0",
          ),
          peak_onbards_mw: parseFloat(plant.peak_onbards_mw?.toString() || "0"),
          date_of_peak_onbards: plant.date_of_peak_onbards,
          slot_of_peak_onbards: plant.slot_of_peak_onbards,
          morning_peak_onbards_mw: parseFloat(
            plant.morning_peak_onbards_mw?.toString() || "0",
          ),
          date_of_morning_peak_onbards: plant.date_of_morning_peak_onbards,
          slot_of_morning_peak_onbards: plant.slot_of_morning_peak_onbards,
          evening_peak_onbards_mw: parseFloat(
            plant.evening_peak_onbards_mw?.toString() || "0",
          ),
          date_of_evening_peak_onbards: plant.date_of_evening_peak_onbards,
          slot_of_evening_peak_onbards: plant.slot_of_evening_peak_onbards,
          average_onbardc_mw: parseFloat(
            plant.average_onbardc_mw?.toString() || "0",
          ),
          peak_onbardc_mw: parseFloat(plant.peak_onbardc_mw?.toString() || "0"),
          date_of_peak_onbardc: plant.date_of_peak_onbardc,
          slot_of_peak_onbardc: plant.slot_of_peak_onbardc,
          morning_peak_onbardc_mw: parseFloat(
            plant.morning_peak_onbardc_mw?.toString() || "0",
          ),
          date_of_morning_peak_onbardc: plant.date_of_morning_peak_onbardc,
          slot_of_morning_peak_onbardc: plant.slot_of_morning_peak_onbardc,
          evening_peak_onbardc_mw: parseFloat(
            plant.evening_peak_onbardc_mw?.toString() || "0",
          ),
          date_of_evening_peak_onbardc: plant.date_of_evening_peak_onbardc,
          slot_of_evening_peak_onbardc: plant.slot_of_evening_peak_onbardc,
          average_backdown_mw: parseFloat(
            plant.average_backdown_mw?.toString() || "0",
          ),
          peak_backdown_mw: parseFloat(
            plant.peak_backdown_mw?.toString() || "0",
          ),
          date_of_peak_backdown: plant.date_of_peak_backdown,
          slot_of_peak_backdown: plant.slot_of_peak_backdown,
          morning_peak_backdown_mw: parseFloat(
            plant.morning_peak_backdown_mw?.toString() || "0",
          ),
          date_of_morning_peak_backdown: plant.date_of_morning_peak_backdown,
          slot_of_morning_peak_backdown: plant.slot_of_morning_peak_backdown,
          evening_peak_backdown_mw: parseFloat(
            plant.evening_peak_backdown_mw?.toString() || "0",
          ),
          date_of_evening_peak_backdown: plant.date_of_evening_peak_backdown,
          slot_of_evening_peak_backdown: plant.slot_of_evening_peak_backdown,
        })) || [],
    }));
  } catch (error) {
    console.error(
      "Error fetching Eltrix Backdown Monthly Source/Plant data:",
      error,
    );
    return [];
  }
}

export async function getReGenerationMonthlySourceArea(
  startDate: string,
  endDate: string,
  energyTypes?: string[],
  areas?: string[],
): Promise<any[]> {
  try {
    let queryParams = `start_date=${startDate}&end_date=${endDate}`;

    if (energyTypes && energyTypes.length > 0) {
      queryParams += `&energy_type=${energyTypes.join(",")}`;
    }
    if (areas && areas.length > 0) {
      queryParams += `&area=${areas.join(",")}`;
    }

    const res = await buildHttpResponse({
      endPoint: `rajasthan/re-generation-monthly-source-area?${queryParams}`,
      method: HttpMethod.GET,
      noCache: true,
    });

    const data = Array.isArray(res) ? res : res?.data || [];
    if (!Array.isArray(data)) {
      console.error("API did not return an array of data:", res);
      return [];
    }

    return data.map((item: any) => ({
      year: item.year,
      month: item.month,
      energy_type: item.energy_type,
      total_areas: item.total_areas,
      source_average_value_mw: parseFloat(
        item.source_average_value_mw?.toString() || "0",
      ),
      source_peak_value_mw: parseFloat(
        item.source_peak_value_mw?.toString() || "0",
      ),
      areas:
        item.areas?.map((area: any) => ({
          area: area.area,
          year: area.year,
          month: area.month,
          energy_type: area.energy_type,
          peak_value_mw: parseFloat(area.peak_value_mw?.toString() || "0"),
          average_value_mw: parseFloat(
            area.average_value_mw?.toString() || "0",
          ),
          date_of_peak_value: area.date_of_peak_value,
          slot_of_peak_value: area.slot_of_peak_value,
          evening_peak_value_mw: parseFloat(
            area.evening_peak_value_mw?.toString() || "0",
          ),
          date_of_evening_peak_value: area.date_of_evening_peak_value,
          slot_of_evening_peak_value: area.slot_of_evening_peak_value,
          morning_peak_value_mw: parseFloat(
            area.morning_peak_value_mw?.toString() || "0",
          ),
          date_of_morning_peak_value: area.date_of_morning_peak_value,
          slot_of_morning_peak_value: area.slot_of_morning_peak_value,
        })) || [],
    }));
  } catch (error) {
    console.error(
      "Error fetching RE Generation Monthly Source/Area data:",
      error,
    );
    return [];
  }
}
export async function getDemoRajasthanDiscomDrawlForecastData({
  startDate,
  endDate,
}: {
  startDate?: string;
  endDate?: string;
}): Promise<RajasthanDiscomDrawal[]> {
  try {
    const randomValue = generateRandomValue();

    const res = await buildHttpResponse({
      endPoint: `rajasthan/getRajasthanDiscomDrawalForecast?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const response = toArray<any[]>(res);
    if (!response || response.length === 0) return [];

    // Columns: [?, date, block, station, value_mw, discom, divisional_headquarter, district]
    //           [0] [1]  [2]   [3]      [4]        [5]     [6]                     [7]

    // derive unique maps
    const uniqueDates = Array.from(new Set(response.map((item) => item[1])));
    const uniqueAreas = Array.from(new Set(response.map((item) => item[3]))); // station
    const uniqueDiscoms = Array.from(new Set(response.map((item) => item[5]))); // discom
    const uniqueDivisions = Array.from(
      new Set(response.map((item) => item[6])),
    ); // divisional_headquarter
    const uniqueDistricts = Array.from(
      new Set(response.map((item) => item[7])),
    ); // district

    // masked maps
    const discomMap: Record<string, string> = {
      JDVVNL: "Discom 1",
      JVVNL: "Discom 2",
      AVVNL: "Discom 3",
    };
    const areaMap: Record<string, string> = {};
    uniqueAreas.forEach((a, i) => (areaMap[a] = `Area ${i + 1}`));
    const divisionMap: Record<string, string> = {};
    uniqueDivisions.forEach((v, i) => (divisionMap[v] = `Division ${i + 1}`));
    const districtMap: Record<string, string> = {};
    uniqueDistricts.forEach((d, i) => (districtMap[d] = `District ${i + 1}`));

    const mapped = response.map((item) => {
      const mw = parseFloat(item[4]) || 0;
      return {
        dateIndex: (uniqueDates.indexOf(item[1]) + 1).toString() + " Day",
        dates: `${startDate} to ${endDate}`,
        date: item[1],
        month: getMonthFromDate(item[1]),
        block: item[2],
        station: areaMap[item[3]] || item[3], // masked GSS station name
        area: divisionMap[item[6]] || item[6], // divisional_headquarter → Group By Area
        discom: discomMap[item[5]] || item[5],
        division: divisionMap[item[6]] || item[6],
        district: districtMap[item[7]] || item[7],
        mw,
        mwh: mw / 4,
        lu: mw / 400,
      };
    });

    return addRandomToNumberFields(mapped, randomValue);
  } catch (error) {
    console.error(
      "Error fetching Rajasthan Discom Drawl Forecast data:",
      error,
    );
    return [];
  }
}
export async function getIsgsMetricsMonthlySourceState(
  startDate: string,
  endDate: string,
  generationSources?: string[],
  states?: string[],
  utilities?: string[],
): Promise<any[]> {
  try {
    let queryParams = `start_date=${startDate}&end_date=${endDate}`;

    if (generationSources && generationSources.length > 0) {
      queryParams += `&generation_source=${generationSources.map((s) => encodeURIComponent(s.toLowerCase())).join(",")}`;
    }
    if (states && states.length > 0) {
      // states are already real names (converted by getRealStateNames before calling)
      queryParams += `&state=${states.map((s) => encodeURIComponent(s)).join(",")}`;
    }
    if (utilities && utilities.length > 0) {
      queryParams += `&utility=${utilities.map((u) => encodeURIComponent(u)).join(",")}`;
    }

    const res = await buildHttpResponse({
      endPoint: `rajasthan/isgs-monthly-source-state-utility?${queryParams}`,
      method: HttpMethod.GET,
      noCache: true,
    });

    const data = Array.isArray(res) ? res : res?.data || [];
    if (!Array.isArray(data)) {
      console.error("API did not return an array of data:", res);
      return [];
    }

    return data.map((item: any) => ({
      year: item.year,
      month: item.month,
      generation_source: item.generation_source,
      total_states: item.total_states,
      total_utilities: parseFloat(item.total_utilities?.toString() || "0"),
      source_average_value_mw: parseFloat(
        item.source_average_value_mw?.toString() || "0",
      ),
      source_peak_value_mw: parseFloat(
        item.source_peak_value_mw?.toString() || "0",
      ),
      states:
        item.states?.map((state: any) => ({
          year: state.year,
          month: state.month,
          state_name: state.state_name,
          total_utilities: state.total_utilities,
          generation_source: state.generation_source,
          state_average_value_mw: parseFloat(
            state.state_average_value_mw?.toString() || "0",
          ),
          state_peak_value_mw: parseFloat(
            state.state_peak_value_mw?.toString() || "0",
          ),
          utilities:
            state.utilities?.map((utility: any) => ({
              year: utility.year,
              month: utility.month,
              state_name: utility.state_name,
              utility_name: utility.utility_name,
              generation_source: utility.generation_source,
              peak_value_mw: parseFloat(
                utility.peak_value_mw?.toString() || "0",
              ),
              average_value_mw: parseFloat(
                utility.average_value_mw?.toString() || "0",
              ),
              date_of_peak_value: utility.date_of_peak_value,
              slot_of_peak_value: utility.slot_of_peak_value,
              evening_peak_value_mw: parseFloat(
                utility.evening_peak_value_mw?.toString() || "0",
              ),
              date_of_evening_peak_value: utility.date_of_evening_peak_value,
              slot_of_evening_peak_value: utility.slot_of_evening_peak_value,
              morning_peak_value_mw: parseFloat(
                utility.morning_peak_value_mw?.toString() || "0",
              ),
              date_of_morning_peak_value: utility.date_of_morning_peak_value,
              slot_of_morning_peak_value: utility.slot_of_morning_peak_value,
            })) || [],
        })) || [],
    }));
  } catch (error) {
    console.error(
      "Error fetching ISGS Monthly Metrics Source/State data:",
      error,
    );
    return [];
  }
}


export async function getGenerationEltrixBackdownMetricsSourcePlant(
  startDate: string,
  endDate: string,
  generationSources?: string[],
  plantNames?: string[],
): Promise<any[]> {
  try {
    // Build query parameters
    let queryParams = `start_date=${startDate}&end_date=${endDate}`;

    // Add generation sources if provided
    if (generationSources && generationSources.length > 0) {
      queryParams += `&generation_source=${generationSources.join(",")}`;
    }

    // Add plant names if provided
    if (plantNames && plantNames.length > 0) {
      queryParams += `&plant_name=${plantNames.join(",")}`;
    }

    const res = await buildHttpResponse({
      endPoint: `rajasthan/eltrix-backdown-metrics-source-plant?${queryParams}`,
      method: HttpMethod.GET,
      noCache: true,
    });

    const data = Array.isArray(res) ? res : res?.data || [];

    if (!Array.isArray(data)) {
      console.error("API did not return an array of data:", res);
      return [];
    }

    return data.map((item: any) => {
      return {
        slot: item.slot,
        generation_source: item.generation_source,
        total_plants: item.total_plants,

        // Source-level metrics
        source_min_onbards: parseFloat(
          item.source_min_onbards?.toString() || "0",
        ),
        source_date_of_min_onbards: item.source_date_of_min_onbards,
        source_max_onbards: parseFloat(
          item.source_max_onbards?.toString() || "0",
        ),
        source_date_of_max_onbards: item.source_date_of_max_onbards,
        source_avg_onbards: parseFloat(
          item.source_avg_onbards?.toString() || "0",
        ),
        source_mode_onbards: parseFloat(
          item.source_mode_onbards?.toString() || "0",
        ),
        source_min_onbardc: parseFloat(
          item.source_min_onbardc?.toString() || "0",
        ),
        source_date_of_min_onbardc: item.source_date_of_min_onbardc,
        source_max_onbardc: parseFloat(
          item.source_max_onbardc?.toString() || "0",
        ),
        source_date_of_max_onbardc: item.source_date_of_max_onbardc,
        source_avg_onbardc: parseFloat(
          item.source_avg_onbardc?.toString() || "0",
        ),
        source_mode_onbardc: parseFloat(
          item.source_mode_onbardc?.toString() || "0",
        ),
        source_min_backdown: parseFloat(
          item.source_min_backdown?.toString() || "0",
        ),
        source_date_of_min_backdown: item.source_date_of_min_backdown,
        source_max_backdown: parseFloat(
          item.source_max_backdown?.toString() || "0",
        ),
        source_date_of_max_backdown: item.source_date_of_max_backdown,
        source_avg_backdown: parseFloat(
          item.source_avg_backdown?.toString() || "0",
        ),
        source_mode_backdown: parseFloat(
          item.source_mode_backdown?.toString() || "0",
        ),

        // Plant-level data
        plants:
          item.plants?.map((plant: any) => ({
            slot: plant.slot,
            generation_source: plant.generation_source,
            plant_name: plant.plant_name,

            // ONBARDS metrics
            min_onbards: parseFloat(plant.min_onbards?.toString() || "0"),
            date_of_min_onbards: plant.date_of_min_onbards,
            max_onbards: parseFloat(plant.max_onbards?.toString() || "0"),
            date_of_max_onbards: plant.date_of_max_onbards,
            avg_onbards: parseFloat(plant.avg_onbards?.toString() || "0"),
            mode_onbards: parseFloat(plant.mode_onbards?.toString() || "0"),

            // ONBARDC metrics
            min_onbardc: parseFloat(plant.min_onbardc?.toString() || "0"),
            date_of_min_onbardc: plant.date_of_min_onbardc,
            max_onbardc: parseFloat(plant.max_onbardc?.toString() || "0"),
            date_of_max_onbardc: plant.date_of_max_onbardc,
            avg_onbardc: parseFloat(plant.avg_onbardc?.toString() || "0"),
            mode_onbardc: parseFloat(plant.mode_onbardc?.toString() || "0"),

            // Backdown metrics
            min_backdown: parseFloat(plant.min_backdown?.toString() || "0"),
            date_of_min_backdown: plant.date_of_min_backdown,
            max_backdown: parseFloat(plant.max_backdown?.toString() || "0"),
            date_of_max_backdown: plant.date_of_max_backdown,
            avg_backdown: parseFloat(plant.avg_backdown?.toString() || "0"),
            mode_backdown: parseFloat(plant.mode_backdown?.toString() || "0"),
          })) || [],
      };
    });
  } catch (error) {
    console.error(
      "Error fetching Eltrix Backdown Metrics Source/Plant data:",
      error,
    );
    return [];
  }
}

export async function getReGenerationMetricsSourceArea(
  startDate: string,
  endDate: string,
  energyTypes?: string[],
  areas?: string[],
): Promise<any[]> {
  try {
    // Build query parameters
    let queryParams = `start_date=${startDate}&end_date=${endDate}`;

    // Add energy types if provided
    if (energyTypes && energyTypes.length > 0) {
      queryParams += `&energy_type=${energyTypes.join(",")}`;
    }

    // Add areas if provided
    if (areas && areas.length > 0) {
      queryParams += `&area=${areas.join(",")}`;
    }

    const res = await buildHttpResponse({
      endPoint: `rajasthan/re-generation-metrics-source-area?${queryParams}`,
      method: HttpMethod.GET,
      noCache: true,
    });

    const data = Array.isArray(res) ? res : res?.data || [];

    if (!Array.isArray(data)) {
      console.error("API did not return an array of data:", res);
      return [];
    }

    return data.map((item: any) => {
      return {
        slot: item.slot,
        energy_type: item.energy_type,
        total_areas: item.total_areas,

        // Source-level metrics
        source_min_value: parseFloat(item.source_min_value?.toString() || "0"),
        source_max_value: parseFloat(item.source_max_value?.toString() || "0"),
        source_avg_value: parseFloat(item.source_avg_value?.toString() || "0"),
        source_mode_value: parseFloat(
          item.source_mode_value?.toString() || "0",
        ),

        // Area-level data
        areas:
          item.areas?.map((area: any) => ({
            area: area.area,
            slot: area.slot,
            energy_type: area.energy_type,

            // Area metrics
            avg_value: parseFloat(area.avg_value?.toString() || "0"),
            max_value: parseFloat(area.max_value?.toString() || "0"),
            min_value: parseFloat(area.min_value?.toString() || "0"),
            mode_value: parseFloat(area.mode_value?.toString() || "0"),
          })) || [],
      };
    });
  } catch (error) {
    console.error(
      "Error fetching RE Generation Metrics Source/Area data:",
      error,
    );
    return [];
  }
}
export async function getIsgsMetricsSourceState(
  startDate: string,
  endDate: string,
  generationSources?: string[],
  states?: string[],
  utilities?: string[],
): Promise<any[]> {
  try {
    // Build query parameters
    let queryParams = `start_date=${startDate}&end_date=${endDate}`;

    // Add generation sources if provided
    if (generationSources && generationSources.length > 0) {
      const encodedSources = generationSources
        .map((s) => encodeURIComponent(s.toLowerCase()))
        .join(",");
      queryParams += `&generation_source=${encodedSources}`;
    }

    // Add states if provided
    if (states && states.length > 0) {
      const encodedStates = states.map((s) => encodeURIComponent(s)).join(",");
      queryParams += `&state=${encodedStates}`;
    }

    // Add utilities if provided
    if (utilities && utilities.length > 0) {
      const encodedUtilities = utilities
        .map((u) => encodeURIComponent(u))
        .join(",");
      queryParams += `&utility=${encodedUtilities}`;
    }

    const res = await buildHttpResponse({
      endPoint: `rajasthan/isgs-metrics-source-state-utility?${queryParams}`,
      method: HttpMethod.GET,
      noCache: true,
    });

    const data = Array.isArray(res) ? res : res?.data || [];

    if (!Array.isArray(data)) {
      console.error("API did not return an array of data:", res);
      return [];
    }

    return data.map((item: any) => {
      return {
        slot: item.slot,
        generation_source: item.generation_source,
        total_states: item.total_states,
        total_utilities: parseFloat(item.total_utilities?.toString() || "0"),

        // Source-level metrics
        source_min_value: parseFloat(item.source_min_value?.toString() || "0"),
        source_max_value: parseFloat(item.source_max_value?.toString() || "0"),
        source_avg_value: parseFloat(item.source_avg_value?.toString() || "0"),
        source_mode_value: parseFloat(
          item.source_mode_value?.toString() || "0",
        ),

        // State-level data
        states:
          item.states?.map((state: any) => ({
            slot: state.slot,
            state_name: state.state_name,
            generation_source: state.generation_source,
            total_utilities: state.total_utilities,

            // State metrics
            state_min_value: parseFloat(
              state.state_min_value?.toString() || "0",
            ),
            state_max_value: parseFloat(
              state.state_max_value?.toString() || "0",
            ),
            state_avg_value: parseFloat(
              state.state_avg_value?.toString() || "0",
            ),
            state_mode_value: parseFloat(
              state.state_mode_value?.toString() || "0",
            ),

            // Utility-level data
            utilities:
              state.utilities?.map((utility: any) => ({
                slot: utility.slot,
                state_name: utility.state_name,
                utility_name: utility.utility_name,
                generation_source: utility.generation_source,

                // Utility metrics
                min_value: parseFloat(utility.min_value?.toString() || "0"),
                max_value: parseFloat(utility.max_value?.toString() || "0"),
                avg_value: parseFloat(utility.avg_value?.toString() || "0"),
                mode_value: parseFloat(utility.mode_value?.toString() || "0"),
              })) || [],
          })) || [],
      };
    });
  } catch (error) {
    console.error("Error fetching ISGS Metrics Source/State data:", error);
    return [];
  }
}